"use client"

// src/components/common/Input/index.tsx
import {
  forwardRef,
  useState
} from "react";
import { combineClasses } from "@shared/utils";
import { INPUT_TYPE } from "@shared/constants";
import EyeIcon from "@shared/icons/EyeIcon";
import EyeCloseIcon from "@shared/icons/EyeCloseIcon";
import { jsx, jsxs } from "react/jsx-runtime";
var Input = forwardRef(
  ({
    className,
    type = INPUT_TYPE.TEXT,
    isInvalid = false,
    onChangeValue,
    endAdornment,
    error,
    rootClassName,
    ...props
  }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === INPUT_TYPE.PASSWORD;
    const handleShowPassword = () => {
      setShowPassword((prev) => !prev);
    };
    const handleChangeValue = (e) => {
      onChangeValue?.(e.target.value);
    };
    return /* @__PURE__ */ jsx("div", { className: combineClasses("relative flex flex-col", rootClassName), children: /* @__PURE__ */ jsxs("div", { className: "relative flex", children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          type: isPassword && showPassword ? INPUT_TYPE.TEXT : type,
          className: combineClasses(
            "flex min-h-[35px] w-full outline-none rounded-md px-3 py-[5.5px]",
            "text-4xs leading-xs transition-colors truncate",
            "bg-frost-100 text-frost-900 font-normal",
            "placeholder:text-frost-400 placeholder:text-4xs",
            "focus:ring-1 focus:ring-primary-500",
            error && "ring-1 ring-destructive focus:ring-destructive",
            "hover:ring-1 hover:ring-frost-900",
            "disabled:cursor-not-allowed disabled:bg-frost-400/30 hover:disabled:ring-0",
            "read-only:bg-frost-400/30 read-only:text-frost-900/70 hover:read-only:ring-0 focus:read-only:ring-0",
            (isPassword || endAdornment) && "pr-10",
            isInvalid && "ring-1 ring-destructive focus:ring-destructive hover:ring-destructive",
            className
          ),
          onChange: handleChangeValue,
          ref,
          ...props
        }
      ),
      isPassword && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          "aria-label": "Toggle password visibility",
          className: combineClasses(
            "absolute right-3 top-1/2 -translate-y-1/2 text-frost-500 rounded-sm",
            "hover:text-frost-900 focus:outline-none focus:ring-1 focus:ring-primary-500"
          ),
          onClick: handleShowPassword,
          children: showPassword ? /* @__PURE__ */ jsx(EyeCloseIcon, { className: "size-4" }) : /* @__PURE__ */ jsx(EyeIcon, { className: "size-4" })
        }
      ),
      /* @__PURE__ */ jsx("div", { className: "absolute right-3 top-1/2 -translate-y-1/2", children: /* @__PURE__ */ jsx("div", { className: "flex-center", children: endAdornment }) })
    ] }) });
  }
);
Input.displayName = "Input";

// src/components/common/Accordion/index.tsx
import {
  forwardRef as forwardRef2
} from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import ChevronDownIcon from "@shared/icons/ChevronDownIcon";
import { combineClasses as combineClasses2 } from "@shared/utils";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var Accordion = AccordionPrimitive.Root;
var AccordionItem = forwardRef2(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  AccordionPrimitive.Item,
  {
    ref,
    className: combineClasses2(className),
    ...props
  }
));
AccordionItem.displayName = "AccordionItem";
var AccordionTrigger = forwardRef2(({ iconClassName, className, children, icon: Icon2, ...props }, ref) => {
  return /* @__PURE__ */ jsxs2(
    AccordionPrimitive.Trigger,
    {
      ref,
      "aria-label": "accordion-trigger",
      className: combineClasses2(
        "flex justify-between items-center w-full transition-all hover:underline text-left [&[data-state=open]>svg]:rotate-180",
        className
      ),
      ...props,
      children: [
        children,
        Icon2 || /* @__PURE__ */ jsx2(
          ChevronDownIcon,
          {
            className: combineClasses2(
              "size-5 shrink-0 text-frost-500 transition-transform duration-200",
              iconClassName
            )
          }
        )
      ]
    }
  );
});
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;
var AccordionContent = forwardRef2(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx2(
  AccordionPrimitive.Content,
  {
    ref,
    className: combineClasses2(
      "shadow-none rounded-sm text-sm transition-all duration-300",
      "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
      className
    ),
    ...props,
    children
  }
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

// src/components/common/Badge/index.tsx
import { cva } from "class-variance-authority";
import { combineClasses as combineClasses3 } from "@shared/utils";

// src/components/common/ImageFallback/index.tsx
import { memo, useEffect, useState as useState2 } from "react";
import Image from "next/image";
import { BLUR_SRC, FALLBACK_SRC } from "@shared/constants";
import { jsx as jsx3 } from "react/jsx-runtime";
var ImageFallback = ({
  blurDataURL = BLUR_SRC.DEFAULT,
  fallbackSrc = FALLBACK_SRC.DEFAULT,
  placeholder,
  src,
  alt,
  width,
  height,
  ...rest
}) => {
  const [imgSrc, setImgSrc] = useState2(() => {
    if (src instanceof Blob) {
      return URL.createObjectURL(src);
    }
    return src;
  });
  const [objectUrl, setObjectUrl] = useState2(null);
  const shouldUseBlur = placeholder === "blur" && blurDataURL;
  useEffect(() => {
    let newSrc;
    if (src instanceof Blob) {
      const blobUrl = URL.createObjectURL(src);
      setObjectUrl(blobUrl);
      newSrc = blobUrl;
    } else {
      newSrc = src;
    }
    setImgSrc(newSrc);
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        setObjectUrl(null);
      }
    };
  }, [src, objectUrl]);
  const handleFallbackImage = () => {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      setObjectUrl(null);
    }
    setImgSrc(fallbackSrc);
  };
  const handleLoad = (event) => {
    if (src instanceof Blob) return;
    const img = event.currentTarget;
    if (img.naturalWidth === 0 || img.naturalHeight === 0 || !img.complete) {
      handleFallbackImage();
      return;
    }
    if (objectUrl && imgSrc === objectUrl) {
      try {
        if (img.width === 0 || img.height === 0) {
          handleFallbackImage();
        }
      } catch (error) {
        console.error(error);
        handleFallbackImage();
      }
    }
  };
  return /* @__PURE__ */ jsx3(
    Image,
    {
      priority: true,
      src: imgSrc,
      alt,
      placeholder: shouldUseBlur ? "blur" : void 0,
      blurDataURL: shouldUseBlur ? blurDataURL : void 0,
      width,
      height,
      onLoad: handleLoad,
      onError: handleFallbackImage,
      ...rest
    }
  );
};
var ImageFallback_default = memo(ImageFallback);

// src/components/common/Badge/index.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var badgeVariants = cva(
  "items-center rounded-md border px-2.5 py-0.5 text-4xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "bg-primary-300 text-slate-700 hover:bg-slate-200 hover:text-slate-900",
        secondary: "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        tertiary: "text-white py-0 bg-coral rounded-sm h-auto px-3 [&_span]:text-[11px] [&_span]:font-normal [&_span]:max-w-full",
        destructive: "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline: "text-foreground",
        active: "text-info-700 py-0 bg-label-450 rounded-sm h-auto px-3 [&_span]:text-[11px] [&_span]:font-normal [&_span]:max-w-full",
        complete: "text-info-800 py-0 bg-label-400 rounded-sm h-auto px-3 [&_span]:text-[11px] [&_span]:font-normal [&_span]:max-w-full",
        waiting: "text-info-200 py-0 bg-label-300 rounded-sm h-auto px-3 [&_span]:text-[11px] [&_span]:font-normal [&_span]:max-w-full",
        pending: "text-info-100 py-0 bg-label-200 rounded-sm h-auto px-3 [&_span]:text-[11px] [&_span]:font-normal [&_span]:max-w-full",
        reopen: "text-info-300 py-0 bg-label-100 rounded-sm h-auto px-3 [&_span]:text-[11px] [&_span]:font-normal [&_span]:max-w-full",
        archive: "text-white py-0 bg-black rounded-sm h-auto px-3 [&_span]:text-[11px] [&_span]:font-normal [&_span]:max-w-full"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
var Badge = ({
  className,
  variant,
  label,
  onDelete,
  deleteIcon,
  isDisabled,
  labelClassName,
  avatar,
  avatarClassName,
  ...props
}) => {
  const handleDelete = (event) => {
    onDelete?.(event);
  };
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      "data-testid": "badge",
      className: combineClasses3(
        "flex items-center gap-1 h-7.5 px-2",
        { "cursor-not-allowed pointer-events-none opacity-50": isDisabled },
        badgeVariants({ variant }),
        className
      ),
      ...props,
      children: [
        !!avatar && /* @__PURE__ */ jsx4(
          ImageFallback_default,
          {
            src: avatar,
            alt: "avatar",
            className: avatarClassName,
            width: 6,
            height: 6
          }
        ),
        /* @__PURE__ */ jsx4(
          "span",
          {
            className: combineClasses3(
              "truncate text-4xs max-w-28 lg:max-w-48 font-semibold",
              labelClassName
            ),
            children: label
          }
        ),
        onDelete && deleteIcon && /* @__PURE__ */ jsx4(
          "button",
          {
            type: "button",
            "aria-label": "Delete button",
            "data-testid": "delete-button",
            onClick: handleDelete,
            disabled: isDisabled,
            className: combineClasses3(
              "rounded-full hover:bg-slate-300 h-4 w-4 inline-flex items-center justify-center",
              { "cursor-not-allowed pointer-events-auto": isDisabled }
            ),
            children: deleteIcon
          }
        )
      ]
    }
  );
};

// src/components/common/Calendar/index.tsx
import { useState as useState3 } from "react";
import { DayPicker } from "react-day-picker";
import { combineClasses as combineClasses4 } from "@shared/utils";
import ChevronLeftIcon from "@shared/icons/ChevronLeftIcon";
import ChevronRightIcon from "@shared/icons/ChevronRightIcon";
import { MONTHS_FULL } from "@shared/constants";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var CALENDAR_CLASSES = {
  base: "p-4 bg-white rounded-lg shadow-sm border border-border pb-2",
  months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
  month: "space-y-6 w-full",
  caption: "flex justify-center pt-1 relative items-center w-full",
  caption_label: "text-md font-medium text-foreground",
  nav: "space-x-1 flex items-center",
  nav_button: "flex items-center justify-center h-8 w-8 hover:bg-accent transition-colors text-muted-foreground hover:text-foreground focus:bg-accent focus:text-foreground",
  nav_button_previous: "absolute left-1",
  nav_button_next: "absolute right-1",
  table: "w-full border-collapse space-y-2",
  head_row: "flex mb-2 justify-between",
  head_cell: "text-muted-foreground rounded-md w-10 font-medium text-3xs",
  row: "flex w-full mt-1 justify-between",
  cell: combineClasses4(
    "[&:has([aria-selected])]:bg-accent/50 rounded-full",
    "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md"
  ),
  day: `
    flex items-center justify-center
    h-9 w-9 p-0
    font-normal rounded-full text-3xs
    transition-colors
    hover:bg-secondary hover:text-secondary-foreground
    focus:outline-none
    disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-accent disabled:cursor-not-allowed
  `,
  day_range_start: "bg-primary text-primary-foreground hover:bg-secondary/90 hover:text-secondary-foreground rounded-l-md",
  day_range_end: "bg-secondary text-secondary-foreground hover:bg-secondary/90 hover:text-secondary-foreground rounded-r-md",
  day_selected: "!bg-secondary !text-secondary-foreground hover:!bg-secondary hover:!text-secondary-foreground focus:bg-secondary focus:text-secondary-foreground",
  day_today: "bg-white text-secondary",
  day_outside: "day-outside text-muted-foreground aria-selected:bg-secondary/50 aria-selected:text-muted-foreground",
  day_disabled: "text-muted-foreground opacity-50 cursor-not-allowed hover:!text-muted-foreground",
  day_range_middle: "aria-selected:bg-secondary aria-selected:text-secondary-foreground",
  day_hidden: "invisible"
};
var COMPACT_CALENDAR_CLASSES = {
  ...CALENDAR_CLASSES,
  caption: "flex justify-between pt-1 items-center w-full",
  nav: "flex items-center gap-6 overflow-hidden",
  day: `
    flex items-center justify-center
    h-9 w-9 p-0
    font-normal rounded-full text-3xs
    transition-colors
    hover:bg-ocean-600 hover:text-white
    focus:outline-none
    disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-accent disabled:cursor-not-allowed
  `,
  day_selected: "!bg-ocean-600 !text-white hover:!bg-ocean-600 hover:!text-white focus:bg-ocean-600 focus:text-white",
  nav_button: "flex items-center justify-center size-6 transition-colors rounded-md text-ocean-200 border-ocean-200 hover:text-ocean-600 hover:border-ocean-600 border-[1.5px]",
  nav_button_previous: "",
  nav_button_next: ""
};
var Calendar = ({
  className,
  showOutsideDays = true,
  onPreviousMonth,
  onNextMonth,
  variant = "default",
  month,
  ...props
}) => {
  const [currentMonth, setCurrentMonth] = useState3(/* @__PURE__ */ new Date());
  const handlePreviousMonthClick = () => onPreviousMonth?.();
  const handleNextMonthClick = () => onNextMonth?.();
  const handleMonthChange = (date) => {
    setCurrentMonth(date);
  };
  const displayMonth = month || currentMonth;
  const monthName = MONTHS_FULL[displayMonth.getMonth()];
  const year = displayMonth.getFullYear();
  const getCalendarClasses = () => {
    switch (variant) {
      case "compact":
        return COMPACT_CALENDAR_CLASSES;
      default:
        return CALENDAR_CLASSES;
    }
  };
  const getCaptionLabel = () => {
    switch (variant) {
      case "compact":
        return () => /* @__PURE__ */ jsxs4("span", { className: "text-ocean-600 font-semibold text-xs", children: [
          monthName,
          " ",
          year
        ] });
      default:
        return () => /* @__PURE__ */ jsx5("span", { className: "text-primary-800 font-semibold uppercase text-xs tracking-[5px]", children: monthName });
    }
  };
  const classes = getCalendarClasses();
  const CaptionLabel = getCaptionLabel();
  const getFormatters = () => {
    switch (variant) {
      case "compact":
        return {
          formatWeekdayName: (date) => date.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase()
        };
      default:
        return void 0;
    }
  };
  const formatters = getFormatters();
  return /* @__PURE__ */ jsx5(
    DayPicker,
    {
      month: displayMonth,
      showOutsideDays,
      onMonthChange: handleMonthChange,
      className: combineClasses4(classes.base, className),
      formatters,
      classNames: {
        months: classes.months,
        month: classes.month,
        caption: classes.caption,
        caption_label: classes.caption_label,
        nav: classes.nav,
        nav_button: classes.nav_button,
        nav_button_previous: classes.nav_button_previous,
        nav_button_next: classes.nav_button_next,
        table: classes.table,
        head_row: classes.head_row,
        head_cell: classes.head_cell,
        row: classes.row,
        cell: classes.cell,
        day: classes.day,
        day_range_start: classes.day_range_start,
        day_range_end: classes.day_range_end,
        day_selected: classes.day_selected,
        day_today: classes.day_today,
        day_outside: classes.day_outside,
        day_disabled: classes.day_disabled,
        day_range_middle: classes.day_range_middle,
        day_hidden: classes.day_hidden
      },
      components: {
        IconLeft: ({ className: className2, ...props2 }) => /* @__PURE__ */ jsx5(
          ChevronLeftIcon,
          {
            "data-testid": "left-icon",
            className: combineClasses4("size-5", className2),
            onClick: handlePreviousMonthClick,
            ...props2
          }
        ),
        CaptionLabel,
        IconRight: ({ className: className2, ...props2 }) => /* @__PURE__ */ jsx5(
          ChevronRightIcon,
          {
            "data-testid": "right-icon",
            className: combineClasses4("size-5", className2),
            onClick: handleNextMonthClick,
            ...props2
          }
        )
      },
      ...props
    }
  );
};
Calendar.displayName = "Calendar";

// src/components/common/Button/index.tsx
import { Slot } from "@radix-ui/react-slot";
import { cva as cva2 } from "class-variance-authority";
import { forwardRef as forwardRef3 } from "react";
import { combineClasses as combineClasses5 } from "@shared/utils";
import LoaderCircleIcon from "@shared/icons/LoaderCircleIcon";
import { jsx as jsx6 } from "react/jsx-runtime";
var buttonVariants = cva2(
  "flex-center gap-2 h-10 px-[18px] whitespace-nowrap rounded-md text-3xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-secondary text-secondary-foreground hover:bg-secondary-100",
        outline: "border border-secondary text-secondary hover:bg-secondary/10",
        disabled: "bg-muted-foreground/20 text-slate-800",
        ghost: "border border-slate-200 text-foreground hover:bg-slate-500/10 dark:bg-slate-700 dark:hover:bg-slate-200/40",
        text: "flex-start text-slate-700 dark:text-foreground gap-2 hover:text-black text-4xs outline-none bg-transparent p-0",
        uppercase: "uppercase bg-secondary text-secondary-foreground hover:bg-secondary-100 rounded-sm",
        primary: "bg-primary-700 rounded-3xl text-primary-foreground h-8 text-4xs font-medium hover:bg-primary-600"
      },
      size: {
        default: "py-3.5 min-w-32",
        full: "w-full",
        sm: "min-w-[107px]",
        lg: "min-w-[280px]",
        fit: "w-fit h-fit",
        icon: "h-5 w-5"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
var Button = forwardRef3(
  ({
    className,
    variant = "default",
    isLoading = false,
    size,
    asChild = false,
    children,
    disabled,
    ...props
  }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx6(
      Comp,
      {
        className: combineClasses5(
          buttonVariants({
            variant: disabled ? "disabled" : variant,
            size,
            className
          })
        ),
        ref,
        disabled: disabled || isLoading,
        ...props,
        children: isLoading ? /* @__PURE__ */ jsx6("div", { className: "flex-center", children: /* @__PURE__ */ jsx6(LoaderCircleIcon, { className: "size-4 w-full animate-spin" }) }) : children
      }
    );
  }
);
Button.displayName = "Button";

// src/components/common/Popover/index.tsx
import { forwardRef as forwardRef4 } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { combineClasses as combineClasses6 } from "@shared/utils";
import { jsx as jsx7 } from "react/jsx-runtime";
var Popover = PopoverPrimitive.Root;
var PopoverTrigger = PopoverPrimitive.Trigger;
var PopoverAnchor = PopoverPrimitive.Anchor;
var PopoverContent = forwardRef4(({ className, align = "center", sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx7(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx7(
  PopoverPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: combineClasses6(
      "z-50 rounded-md border bg-popover p-4",
      "text-popover-foreground shadow-xl shadow-black/40 outline-none",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
      "data-[side=bottom]:slide-in-from-top-2",
      "data-[side=left]:slide-in-from-right-2",
      "data-[side=right]:slide-in-from-left-2",
      "data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

// src/components/common/Dialog/base/index.ts
import * as DialogPrimitive5 from "@radix-ui/react-dialog";

// src/components/common/Dialog/base/DialogOverlay.tsx
import { forwardRef as forwardRef5 } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { combineClasses as combineClasses7 } from "@shared/utils";
import { jsx as jsx8 } from "react/jsx-runtime";
var DialogOverlay = forwardRef5(({ className, ...props }, ref) => /* @__PURE__ */ jsx8(
  DialogPrimitive.Overlay,
  {
    ref,
    className: combineClasses7(
      "fixed inset-0 z-40 bg-black/80",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props
  }
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// src/components/common/Dialog/base/DialogContent.tsx
import * as DialogPrimitive2 from "@radix-ui/react-dialog";
import { forwardRef as forwardRef6 } from "react";
import { combineClasses as combineClasses8 } from "@shared/utils";
import CloseIcon from "@shared/icons/CloseIcon";
import { jsx as jsx9, jsxs as jsxs5 } from "react/jsx-runtime";
var DialogContent = forwardRef6(
  ({ className, children, overlayClassName, closeBtnClassName, ...props }, ref) => /* @__PURE__ */ jsxs5(DialogPrimitive2.Portal, { children: [
    /* @__PURE__ */ jsx9(DialogOverlay, { className: overlayClassName }),
    /* @__PURE__ */ jsxs5(
      DialogPrimitive2.Content,
      {
        ref,
        className: combineClasses8(
          "fixed left-[50%] top-[50%]",
          "z-50 w-[95vw] max-w-base-modal-sm sm:max-w-base-modal",
          "translate-x-[-50%] translate-y-[-50%]",
          "gap-4 border bg-background shadow-lg",
          "px-6 py-8 sm:rounded-lg duration-200",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
          "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
          className
        ),
        ...props,
        children: [
          children,
          /* @__PURE__ */ jsxs5(
            DialogPrimitive2.Close,
            {
              className: combineClasses8(
                "absolute right-3 top-3 rounded-md opacity-70",
                "ring-offset-background transition-opacity p-0.5",
                "hover:opacity-100 hover:outline-none hover:ring-2 hover:ring-slate-500 hover:ring-offset-2 disabled:pointer-events-none",
                "data-[state=open]:bg-accent data-[state=open]:text-muted-foreground",
                closeBtnClassName
              ),
              children: [
                /* @__PURE__ */ jsx9(CloseIcon, { className: "size-5" }),
                /* @__PURE__ */ jsx9("span", { className: "sr-only", children: "Close" })
              ]
            }
          )
        ]
      }
    )
  ] })
);
DialogContent.displayName = DialogPrimitive2.Content.displayName;

// src/components/common/Dialog/base/DialogHeader.tsx
import { combineClasses as combineClasses9 } from "@shared/utils";
import { jsx as jsx10 } from "react/jsx-runtime";
var DialogHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx10(
  "div",
  {
    className: combineClasses9(
      "flex flex-col space-y-1.5",
      "text-center sm:text-left",
      className
    ),
    ...props
  }
);
DialogHeader.displayName = "DialogHeader";

// src/components/common/Dialog/base/DialogFooter.tsx
import { combineClasses as combineClasses10 } from "@shared/utils";
import { jsx as jsx11 } from "react/jsx-runtime";
var DialogFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx11(
  "div",
  {
    className: combineClasses10(
      "flex flex-col-reverse sm:flex-row",
      "sm:justify-end sm:space-x-2",
      className
    ),
    ...props
  }
);
DialogFooter.displayName = "DialogFooter";

// src/components/common/Dialog/base/DialogTitle.tsx
import * as DialogPrimitive3 from "@radix-ui/react-dialog";
import { forwardRef as forwardRef7 } from "react";
import { combineClasses as combineClasses11 } from "@shared/utils";
import { jsx as jsx12 } from "react/jsx-runtime";
var DialogTitle = forwardRef7(({ className, ...props }, ref) => /* @__PURE__ */ jsx12(
  DialogPrimitive3.Title,
  {
    ref,
    className: combineClasses11(
      "text-lg font-semibold leading-none tracking-tight",
      className
    ),
    ...props
  }
));
DialogTitle.displayName = DialogPrimitive3.Title.displayName;

// src/components/common/Dialog/base/DialogDescription.tsx
import * as DialogPrimitive4 from "@radix-ui/react-dialog";
import { forwardRef as forwardRef8 } from "react";
import { combineClasses as combineClasses12 } from "@shared/utils";
import { jsx as jsx13 } from "react/jsx-runtime";
var DialogDescription = forwardRef8(({ className, ...props }, ref) => /* @__PURE__ */ jsx13(
  DialogPrimitive4.Description,
  {
    ref,
    className: combineClasses12("text-md text-slate-500/80", className),
    ...props
  }
));
DialogDescription.displayName = DialogPrimitive4.Description.displayName;

// src/components/common/Dialog/base/index.ts
var DialogBase = DialogPrimitive5.Root;
var DialogTrigger = DialogPrimitive5.Trigger;
var DialogPortal = DialogPrimitive5.Portal;
var DialogClose = DialogPrimitive5.Close;

// src/components/common/Dialog/CustomDialog/index.tsx
import { combineClasses as combineClasses13 } from "@shared/utils";
import { jsx as jsx14, jsxs as jsxs6 } from "react/jsx-runtime";
var DIALOG_CLASSES = {
  title: "text-2xl font-semibold",
  description: "hidden mt-2"
};
var CustomDialog = ({
  title,
  description,
  open,
  onOpenChange,
  hideCloseButton,
  children,
  trigger,
  className,
  titleClassName,
  contentClassName,
  headerClassName,
  overlayClassName,
  closeBtnClassName,
  footer,
  onCloseAutoFocus
}) => {
  return /* @__PURE__ */ jsxs6(DialogBase, { open, onOpenChange, children: [
    trigger && /* @__PURE__ */ jsx14(DialogTrigger, { asChild: true, children: trigger }),
    /* @__PURE__ */ jsxs6(
      DialogContent,
      {
        "data-testid": "dialog-content-container",
        className: combineClasses13(
          "rounded-md",
          hideCloseButton && "[&>button]:hidden",
          className
        ),
        closeBtnClassName,
        onCloseAutoFocus,
        overlayClassName,
        children: [
          /* @__PURE__ */ jsxs6(DialogHeader, { "data-testid": "dialog-header", className: headerClassName, children: [
            /* @__PURE__ */ jsx14(
              DialogTitle,
              {
                className: combineClasses13(DIALOG_CLASSES.title, titleClassName),
                children: title
              }
            ),
            /* @__PURE__ */ jsx14(
              DialogDescription,
              {
                className: combineClasses13(
                  DIALOG_CLASSES.description,
                  description && "flex"
                ),
                children: description
              }
            )
          ] }),
          children && /* @__PURE__ */ jsx14(
            "div",
            {
              "data-testid": "dialog-content",
              className: combineClasses13(contentClassName),
              children
            }
          ),
          footer
        ]
      }
    )
  ] });
};
CustomDialog.displayName = "CustomDialog";

// src/components/common/Dialog/ConfirmDialog/index.tsx
import { combineClasses as combineClasses14 } from "@shared/utils";
import { jsx as jsx15, jsxs as jsxs7 } from "react/jsx-runtime";
var CONFIRM_DIALOG_CLASSES = {
  footer: "mt-4 sm:mt-8 gap-2 sm:gap-4",
  cancelButton: "",
  submitButton: "bg-primary/90 font-semibold hover:bg-primary"
};
var ConfirmDialog = ({
  title,
  description,
  trigger,
  children,
  submitText = "Confirm",
  cancelText = "Cancel",
  cancelTextClassName,
  submitTextClassName,
  footerClassName,
  titleClassName,
  onSubmit,
  submitElement,
  cancelElement,
  ...props
}) => {
  const handleSubmit = async () => {
    await onSubmit();
  };
  return /* @__PURE__ */ jsx15(
    CustomDialog,
    {
      title,
      titleClassName,
      description,
      trigger,
      footer: /* @__PURE__ */ jsxs7(
        DialogFooter,
        {
          "data-testid": "dialog-footer",
          className: combineClasses14(
            CONFIRM_DIALOG_CLASSES.footer,
            footerClassName
          ),
          children: [
            /* @__PURE__ */ jsx15(DialogClose, { asChild: true, children: cancelElement || /* @__PURE__ */ jsx15(
              Button,
              {
                variant: "outline",
                "aria-label": "Cancel confirmation",
                className: combineClasses14(
                  CONFIRM_DIALOG_CLASSES.cancelButton,
                  cancelTextClassName
                ),
                children: cancelText
              }
            ) }),
            /* @__PURE__ */ jsx15(DialogClose, { asChild: true, children: submitElement || /* @__PURE__ */ jsx15(
              Button,
              {
                onClick: handleSubmit,
                "aria-label": "Confirm action",
                className: combineClasses14(
                  CONFIRM_DIALOG_CLASSES.submitButton,
                  submitTextClassName
                ),
                children: submitText
              }
            ) })
          ]
        }
      ),
      ...props,
      children
    }
  );
};
ConfirmDialog.displayName = "ConfirmDialog";

// src/components/common/Command/index.tsx
import {
  forwardRef as forwardRef9
} from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Command as CommandPrimitive } from "cmdk";
import { combineClasses as combineClasses15 } from "@shared/utils";
import { jsx as jsx16, jsxs as jsxs8 } from "react/jsx-runtime";
var Command = forwardRef9(({ className, ...props }, ref) => /* @__PURE__ */ jsx16(
  CommandPrimitive,
  {
    ref,
    className: combineClasses15(
      "flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground",
      className
    ),
    ...props
  }
));
Command.displayName = CommandPrimitive.displayName;
var CommandDialog = ({ children, ...props }) => {
  return /* @__PURE__ */ jsx16(DialogBase, { ...props, children: /* @__PURE__ */ jsxs8(DialogContent, { className: "overflow-hidden p-0", children: [
    /* @__PURE__ */ jsx16(VisuallyHidden, { asChild: true, children: /* @__PURE__ */ jsx16(DialogDescription, {}) }),
    /* @__PURE__ */ jsx16(VisuallyHidden, { asChild: true, children: /* @__PURE__ */ jsx16(DialogTitle, {}) }),
    /* @__PURE__ */ jsx16(
      Command,
      {
        className: combineClasses15(
          "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-item]]:py-3",
          "[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2",
          "[&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2",
          "[&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
        ),
        children
      }
    )
  ] }) });
};
var CommandInput = forwardRef9(({ className, ...props }, ref) => /* @__PURE__ */ jsx16(
  CommandPrimitive.Input,
  {
    ref,
    className: combineClasses15(
      "flex min-h-[35px] w-full rounded-md bg-transparent py-3 outline-none truncate",
      "text-4xs leading-xs text-frost-900 placeholder:text-frost-500",
      "disabled:cursor-not-allowed disabled:opacity-50",
      className
    ),
    ...props
  }
));
CommandInput.displayName = CommandPrimitive.Input.displayName;
var CommandList = forwardRef9(({ className, ...props }, ref) => /* @__PURE__ */ jsx16(
  CommandPrimitive.List,
  {
    ref,
    className: combineClasses15(
      "max-h-[300px] overflow-y-auto overflow-x-hidden",
      className
    ),
    ...props
  }
));
CommandList.displayName = CommandPrimitive.List.displayName;
var CommandEmpty = forwardRef9((props, ref) => /* @__PURE__ */ jsx16(
  CommandPrimitive.Empty,
  {
    ref,
    className: "py-6 text-center text-sm",
    ...props
  }
));
CommandEmpty.displayName = CommandPrimitive.Empty.displayName;
var CommandGroup = forwardRef9(({ className, ...props }, ref) => /* @__PURE__ */ jsx16(
  CommandPrimitive.Group,
  {
    ref,
    className: combineClasses15(
      "overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5",
      "[&_[cmdk-group-heading]]:text-3xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-neutral-300",
      className
    ),
    ...props
  }
));
CommandGroup.displayName = CommandPrimitive.Group.displayName;
var CommandSeparator = forwardRef9(({ className, ...props }, ref) => /* @__PURE__ */ jsx16(
  CommandPrimitive.Separator,
  {
    ref,
    className: combineClasses15("-mx-1 h-px bg-border", className),
    ...props
  }
));
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;
var CommandItem = forwardRef9(({ className, ...props }, ref) => /* @__PURE__ */ jsx16(
  CommandPrimitive.Item,
  {
    ref,
    className: combineClasses15(
      "relative flex cursor-pointer gap-2 select-none items-center rounded-sm px-2 py-1.5 text-4xs",
      "outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-primary/80",
      "data-[selected=true]:text-white data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      className
    ),
    ...props
  }
));
CommandItem.displayName = CommandPrimitive.Item.displayName;
var CommandShortcut = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx16(
    "span",
    {
      className: combineClasses15(
        "ml-auto text-xs tracking-widest text-muted-foreground",
        className
      ),
      ...props
    }
  );
};
CommandShortcut.displayName = "CommandShortcut";

// src/components/common/DropdownMenu/index.tsx
import {
  forwardRef as forwardRef10
} from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { combineClasses as combineClasses16 } from "@shared/utils";
import CheckIcon from "@shared/icons/CheckIcon";
import ChevronRightIcon2 from "@shared/icons/ChevronRightIcon";
import CircleIcon from "@shared/icons/CircleIcon";
import { jsx as jsx17, jsxs as jsxs9 } from "react/jsx-runtime";
var DropdownMenu = DropdownMenuPrimitive.Root;
var DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
var DropdownMenuGroup = DropdownMenuPrimitive.Group;
var DropdownMenuPortal = DropdownMenuPrimitive.Portal;
var DropdownMenuSub = DropdownMenuPrimitive.Sub;
var DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
var DropdownMenuSubTrigger = forwardRef10(({ className, inset, children, ...props }, ref) => /* @__PURE__ */ jsxs9(
  DropdownMenuPrimitive.SubTrigger,
  {
    ref,
    className: combineClasses16(
      "flex cursor-default select-none items-center gap-2 rounded-sm px-2",
      "py-1.5 text-sm outline-none focus:bg-accent data-[state=open]:bg-accent",
      "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
      inset && "pl-8",
      className
    ),
    ...props,
    children: [
      children,
      /* @__PURE__ */ jsx17(ChevronRightIcon2, { className: "ml-auto" })
    ]
  }
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
var DropdownMenuSubContent = forwardRef10(({ className, ...props }, ref) => /* @__PURE__ */ jsx17(
  DropdownMenuPrimitive.SubContent,
  {
    ref,
    className: combineClasses16(
      "z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover",
      "p-1 text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95",
      "data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2",
      "data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2",
      "data-[side=top]:slide-in-from-bottom-2 origin-[--radix-dropdown-menu-content-transform-origin]",
      className
    ),
    ...props
  }
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
var DropdownMenuContent = forwardRef10(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx17(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx17(
  DropdownMenuPrimitive.Content,
  {
    ref,
    sideOffset,
    className: combineClasses16(
      "z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] min-w-[8rem]",
      "overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
      "overflow-y-auto data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95",
      "data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2",
      "data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2",
      "data-[side=top]:slide-in-from-bottom-2 origin-[--radix-dropdown-menu-content-transform-origin]",
      className
    ),
    ...props
  }
) }));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
var DropdownMenuItem = forwardRef10(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx17(
  DropdownMenuPrimitive.Item,
  {
    ref,
    className: combineClasses16(
      "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2",
      "py-1.5 text-sm outline-none transition-colors focus:bg-accent",
      "focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      "[&>svg]:size-4 [&>svg]:shrink-0",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
var DropdownMenuCheckboxItem = forwardRef10(({ className, children, checked, ...props }, ref) => /* @__PURE__ */ jsxs9(
  DropdownMenuPrimitive.CheckboxItem,
  {
    ref,
    className: combineClasses16(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5",
      "pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent",
      "focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    checked,
    ...props,
    children: [
      /* @__PURE__ */ jsx17("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx17(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx17(CheckIcon, { className: "size-4" }) }) }),
      children
    ]
  }
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;
var DropdownMenuRadioItem = forwardRef10(({ className, children, ...props }, ref) => /* @__PURE__ */ jsxs9(
  DropdownMenuPrimitive.RadioItem,
  {
    ref,
    className: combineClasses16(
      "relative flex cursor-default select-none items-center rounded-sm py-1.5",
      "pl-8 pr-2 text-sm outline-none transition-colors focus:bg-accent",
      "focus:text-accent-foreground data-[disabled]:pointer-events-none",
      "data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      /* @__PURE__ */ jsx17("span", { className: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center", children: /* @__PURE__ */ jsx17(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx17(CircleIcon, { className: "size-2 fill-current" }) }) }),
      children
    ]
  }
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
var DropdownMenuLabel = forwardRef10(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx17(
  DropdownMenuPrimitive.Label,
  {
    ref,
    className: combineClasses16(
      "px-2 py-1.5 text-sm font-semibold",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
var DropdownMenuSeparator = forwardRef10(({ className, ...props }, ref) => /* @__PURE__ */ jsx17(
  DropdownMenuPrimitive.Separator,
  {
    ref,
    className: combineClasses16("-mx-1 my-1 h-px bg-muted", className),
    ...props
  }
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
var DropdownMenuShortcut = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx17(
    "span",
    {
      className: combineClasses16(
        "ml-auto text-xs tracking-widest opacity-60",
        className
      ),
      ...props
    }
  );
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

// src/components/common/Card/index.tsx
import Link from "next/link";
import { combineClasses as combineClasses18 } from "@shared/utils";

// src/components/common/Typography/index.tsx
import { forwardRef as forwardRef11 } from "react";
import { combineClasses as combineClasses17 } from "@shared/utils";
import { jsx as jsx18 } from "react/jsx-runtime";
var TYPOGRAPHY_SIZES = {
  "4xs": "text-4xs leading-xs",
  //13px : 24px
  "3xs": "text-3xs leading-xs",
  //14px : 24px
  "2xs": "text-2xs leading-2xs",
  //15px : 22px
  xs: "text-xs leading-xs",
  //16px : 24px
  sm: "text-sm leading-sm",
  //17px : 30px
  md: "text-md leading-md",
  //18px : 32px
  lg: "text-lg leading-lg",
  //20px : 48px
  xl: "text-xl leading-xl",
  //22px : 50px
  "2xl": "text-2xl leading-2xl",
  //24px : 70px
  "3xl": "text-3xl leading-3xl",
  //26px : 80px
  "4xl": "text-4xl leading-3xl",
  //36px : 80px
  "5xl": "text-5xl leading-3xl",
  //40px : 80px
  "6xl": "text-6xl leading-3xl",
  //48px : 80px
  "7xl": "text-7xl leading-3xl"
  //60px : 80px
};
var TYPOGRAPHY_WEIGHTS = {
  light: "font-light",
  // 300
  normal: "font-normal",
  // 400
  medium: "font-medium",
  // 500
  semibold: "font-semibold",
  // 600
  bold: "font-bold"
  // 700
};
var TYPOGRAPHY_LINE_CLAMP = {
  none: "",
  "1": "line-clamp-1",
  "2": "line-clamp-2",
  "3": "line-clamp-3",
  "4": "line-clamp-4",
  "5": "line-clamp-5",
  "6": "line-clamp-6"
};
var TYPOGRAPHY_COLORS = {
  primary: "text-foreground",
  secondary: "text-muted-foreground",
  success: "text-success",
  error: "text-destructive"
};
var Typography = forwardRef11(
  ({
    size = "md",
    weight = "normal",
    color = "primary",
    as: Component = "p",
    lineClamp = "none",
    truncate = false,
    className,
    children,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx18(
      Component,
      {
        ref,
        className: combineClasses17(
          TYPOGRAPHY_SIZES[size],
          TYPOGRAPHY_WEIGHTS[weight],
          TYPOGRAPHY_COLORS[color],
          TYPOGRAPHY_LINE_CLAMP[lineClamp],
          truncate && "truncate",
          className
        ),
        ...props,
        children
      }
    );
  }
);
Typography.displayName = "Typography";
var Typography_default = Typography;

// src/components/common/Card/index.tsx
import { Fragment, jsx as jsx19, jsxs as jsxs10 } from "react/jsx-runtime";
var Card = ({
  title = "",
  description = "",
  buttonLink = {
    btnName: "",
    btnLink: "",
    btnClass: "",
    btnIcon: null
  },
  titleClassName = "",
  descriptionClassName = "",
  className = "",
  children
}) => {
  const { btnName, btnLink, btnClass, btnIcon } = buttonLink;
  return /* @__PURE__ */ jsxs10("div", { className: combineClasses18("flex flex-col gap-4", className), children: [
    title && /* @__PURE__ */ jsx19(
      Typography_default,
      {
        as: "h2",
        size: "lg",
        className: combineClasses18("font-medium", titleClassName),
        children: title
      }
    ),
    (children || description) && /* @__PURE__ */ jsxs10(Fragment, { children: [
      description && /* @__PURE__ */ jsx19(Typography_default, { as: "p", className: combineClasses18(descriptionClassName), children: description }),
      children
    ] }),
    btnLink && /* @__PURE__ */ jsxs10(
      Link,
      {
        href: btnLink,
        className: combineClasses18(
          "px-2 md:px-8 py-3 min-h-8 leading-8 text-3xs sm:text-sm md:text-md lg:text-lg font-bold min-w-30 sm:max-w-80 md:max-w-btn-hero min-h-btn bg-primary text-primary-foreground hover:bg-destructive rounded-md text-center",
          btnClass
        ),
        children: [
          btnIcon,
          btnName
        ]
      }
    )
  ] });
};

// src/components/common/Separator/index.tsx
import { forwardRef as forwardRef12 } from "react";
import * as SeparatorPrimitive from "@radix-ui/react-separator";
import { combineClasses as combineClasses19 } from "@shared/utils";
import { jsx as jsx20 } from "react/jsx-runtime";
var Separator2 = forwardRef12(
  ({ className, orientation = "horizontal", decorative = true, ...props }, ref) => /* @__PURE__ */ jsx20(
    SeparatorPrimitive.Root,
    {
      ref,
      decorative,
      orientation,
      className: combineClasses19(
        "shrink-0 bg-border",
        orientation === "horizontal" ? "h-[1px] w-full" : "h-full w-[1px]",
        className
      ),
      ...props
    }
  )
);
Separator2.displayName = SeparatorPrimitive.Root.displayName;

// src/components/common/Tooltip/index.tsx
import { cva as cva3 } from "class-variance-authority";
import { combineClasses as combineClasses20 } from "@shared/utils";
import { jsx as jsx21, jsxs as jsxs11 } from "react/jsx-runtime";
var tooltipVariants = cva3(
  "hidden absolute group-hover:block group-focus-visible:block w-max text-white text-[11px] rounded-xs py-1 px-2.5",
  {
    variants: {
      variant: {
        top: "bottom-full mb-2 left-1/2 -translate-x-1/2 [&_span]:top-full [&_span]:left-1/2 [&_span]:-translate-x-1/2 [&_span]:border-x-transparent [&_span]:border-b-transparent",
        bottom: "top-full mt-2 left-1/2 -translate-x-1/2 [&_span]:bottom-full [&_span]:left-1/2 [&_span]:-translate-y-0 [&_span]:border-x-transparent [&_span]:border-t-transparent",
        left: "right-full mr-2 top-1/2 -translate-y-1/2 [&_span]:top-1/2 [&_span]:-translate-y-1/2 [&_span]:-right-2 [&_span]:border-y-transparent [&_span]:border-r-transparent",
        right: "left-full ml-2 top-1/2 -translate-y-1/2 [&_span]:top-1/2 [&_span]:-translate-y-1/2 [&_span]:-left-2 [&_span]:border-y-transparent [&_span]:border-l-transparent"
      }
    },
    defaultVariants: {
      variant: "top"
    }
  }
);
var Tooltip = ({
  variant = "top",
  content = "Tooltip content",
  rootClassName = "",
  arrowClassName = "",
  className = "",
  children
}) => {
  return /* @__PURE__ */ jsxs11(
    "div",
    {
      "data-testid": "tooltip",
      className: combineClasses20("group relative outline-none", rootClassName),
      children: [
        children,
        /* @__PURE__ */ jsxs11(
          "div",
          {
            "data-testid": "tooltip-content",
            className: combineClasses20(
              "bg-midnight",
              tooltipVariants({ variant }),
              className
            ),
            children: [
              content,
              /* @__PURE__ */ jsx21(
                "span",
                {
                  "data-testid": "tooltip-arrow",
                  className: combineClasses20(
                    "absolute w-0 h-0 border-4 border-midnight",
                    arrowClassName
                  )
                }
              )
            ]
          }
        )
      ]
    }
  );
};

// src/components/common/Switch/index.tsx
import * as SwitchPrimitive from "@radix-ui/react-switch";
import { combineClasses as combineClasses21 } from "@shared/utils";
import { jsx as jsx22 } from "react/jsx-runtime";
var Switch = ({
  className,
  thumbClassName,
  ...props
}) => /* @__PURE__ */ jsx22(
  SwitchPrimitive.Root,
  {
    "data-slot": "switch",
    className: combineClasses21(
      "peer data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
      "focus-visible:border-ring focus-visible:ring-ring/50 dark:data-[state=unchecked]:bg-input/80",
      "inline-flex h-[1.15rem] w-8 shrink-0 items-center rounded-full border border-transparent",
      "shadow-xs transition-all outline-none focus-visible:ring-[3px]",
      "disabled:cursor-not-allowed disabled:opacity-50",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx22(
      SwitchPrimitive.Thumb,
      {
        "data-slot": "switch-thumb",
        className: combineClasses21(
          "bg-background dark:data-[state=unchecked]:bg-foreground",
          "dark:data-[state=checked]:bg-primary-foreground pointer-events-none block",
          "size-4 rounded-full ring-0 transition-transform data-[state=checked]:translate-x-[calc(100%-2px)]",
          "data-[state=unchecked]:translate-x-0",
          thumbClassName
        )
      }
    )
  }
);

// src/components/common/TruncatedText/index.tsx
import { forwardRef as forwardRef13 } from "react";
import { useTruncation } from "@shared/hooks";
import { combineClasses as combineClasses22 } from "@shared/utils";
import Link2 from "next/link";
import { jsx as jsx23 } from "react/jsx-runtime";
var TruncatedText = forwardRef13(
  ({
    text,
    href,
    showTooltip = true,
    tooltipContent,
    tooltipVariant = "top",
    tooltipClassName,
    tooltipRootClassName,
    ...typographyProps
  }, ref) => {
    const { elementRef, isTruncated } = useTruncation(text);
    const mergedRef = (node) => {
      if (elementRef.current !== node) {
        elementRef.current = node;
      }
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };
    const { as: _as, ...linkCompatibleProps } = typographyProps;
    if (!showTooltip || !isTruncated) {
      return href ? /* @__PURE__ */ jsx23(Link2, { href, ref: mergedRef, ...linkCompatibleProps, children: text }) : /* @__PURE__ */ jsx23(Typography_default, { ref: mergedRef, ...typographyProps, children: text });
    }
    return href ? /* @__PURE__ */ jsx23(
      Tooltip,
      {
        content: tooltipContent || text,
        variant: tooltipVariant,
        className: combineClasses22("max-w-56 text-wrap", tooltipClassName),
        rootClassName: tooltipRootClassName,
        children: /* @__PURE__ */ jsx23(Link2, { href, ref: mergedRef, ...linkCompatibleProps, children: text })
      }
    ) : /* @__PURE__ */ jsx23(
      Tooltip,
      {
        content: tooltipContent || text,
        variant: tooltipVariant,
        className: combineClasses22("max-w-56 text-wrap", tooltipClassName),
        rootClassName: tooltipRootClassName,
        children: /* @__PURE__ */ jsx23(Typography_default, { ref: mergedRef, ...typographyProps, children: text })
      }
    );
  }
);
TruncatedText.displayName = "TruncatedText";
var TruncatedText_default = TruncatedText;

// src/components/common/BrandIcon/index.tsx
import Link3 from "next/link";
import { useEffect as useEffect2, useState as useState4 } from "react";
import { useTheme } from "next-themes";
import { THEME } from "@shared/constants";
import { jsx as jsx24 } from "react/jsx-runtime";
var BrandIcon = ({ href, alt, src, width, height }) => {
  const { lightMode, darkMode = lightMode } = src;
  const [mounted, setMounted] = useState4(false);
  const { theme } = useTheme();
  useEffect2(() => {
    setMounted(true);
  }, []);
  if (!mounted) {
    return null;
  }
  const themeLogo = theme === THEME.LIGHT ? lightMode : darkMode;
  return /* @__PURE__ */ jsx24(Link3, { href, children: /* @__PURE__ */ jsx24(ImageFallback_default, { src: themeLogo, alt, width, height }) });
};
var BrandIcon_default = BrandIcon;

// src/components/common/Skeleton/index.tsx
import { combineClasses as combineClasses23 } from "@shared/utils";
import { jsx as jsx25 } from "react/jsx-runtime";
var Skeleton = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx25(
  "div",
  {
    "data-testid": "skeleton",
    className: combineClasses23(
      "animate-pulse rounded-md bg-gray-500/10",
      className
    ),
    ...props
  }
);
var Skeleton_default = Skeleton;

// src/components/common/Select/base/Select.tsx
import * as SelectPrimitive from "@radix-ui/react-select";
var Select = SelectPrimitive.Root;
var Select_default = Select;

// src/components/common/Select/base/SelectContent.tsx
import { forwardRef as forwardRef16 } from "react";
import * as SelectPrimitive4 from "@radix-ui/react-select";
import { combineClasses as combineClasses26 } from "@shared/utils";

// src/components/common/Select/base/SelectScrollUpButton.tsx
import { forwardRef as forwardRef14 } from "react";
import * as SelectPrimitive2 from "@radix-ui/react-select";
import { combineClasses as combineClasses24 } from "@shared/utils";
import ChevronUpIcon from "@shared/icons/ChevronUpIcon";
import { jsx as jsx26 } from "react/jsx-runtime";
var SelectScrollUpButton = forwardRef14(({ className, ...props }, ref) => /* @__PURE__ */ jsx26(
  SelectPrimitive2.ScrollUpButton,
  {
    ref,
    className: combineClasses24(
      "flex cursor-default items-center justify-center py-1",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx26(ChevronUpIcon, { className: "size-4" })
  }
));
SelectScrollUpButton.displayName = SelectPrimitive2.ScrollUpButton.displayName;
var SelectScrollUpButton_default = SelectScrollUpButton;

// src/components/common/Select/base/SelectScrollDownButton.tsx
import { forwardRef as forwardRef15 } from "react";
import * as SelectPrimitive3 from "@radix-ui/react-select";
import ChevronDownIcon2 from "@shared/icons/ChevronDownIcon";
import { combineClasses as combineClasses25 } from "@shared/utils";
import { jsx as jsx27 } from "react/jsx-runtime";
var SelectScrollDownButton = forwardRef15(({ className, ...props }, ref) => /* @__PURE__ */ jsx27(
  SelectPrimitive3.ScrollDownButton,
  {
    ref,
    className: combineClasses25(
      "flex cursor-default items-center justify-center py-1",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx27(ChevronDownIcon2, { className: "size-4" })
  }
));
SelectScrollDownButton.displayName = SelectPrimitive3.ScrollDownButton.displayName;
var SelectScrollDownButton_default = SelectScrollDownButton;

// src/components/common/Select/base/SelectContent.tsx
import { jsx as jsx28, jsxs as jsxs12 } from "react/jsx-runtime";
var SelectContent = forwardRef16(({ className, children, position = "popper", ...props }, ref) => /* @__PURE__ */ jsx28(SelectPrimitive4.Portal, { children: /* @__PURE__ */ jsxs12(
  SelectPrimitive4.Content,
  {
    ref,
    className: combineClasses26(
      "relative z-50 max-h-96 min-w-[8rem] overflow-hidden",
      "rounded-md border bg-popover text-popover-foreground shadow-md",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
      "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
      "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
      className
    ),
    position,
    ...props,
    children: [
      /* @__PURE__ */ jsx28(SelectScrollUpButton_default, {}),
      /* @__PURE__ */ jsx28(
        SelectPrimitive4.Viewport,
        {
          className: combineClasses26(
            "flex flex-col gap-0.5 p-1",
            position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
          ),
          children
        }
      ),
      /* @__PURE__ */ jsx28(SelectScrollDownButton_default, {})
    ]
  }
) }));
SelectContent.displayName = SelectPrimitive4.Content.displayName;
var SelectContent_default = SelectContent;

// src/components/common/Select/base/SelectGroup.tsx
import * as SelectPrimitive5 from "@radix-ui/react-select";

// src/components/common/Select/base/SelectItem.tsx
import { forwardRef as forwardRef17 } from "react";
import * as SelectPrimitive6 from "@radix-ui/react-select";
import CheckIcon2 from "@shared/icons/CheckIcon";
import { combineClasses as combineClasses27 } from "@shared/utils";
import { jsx as jsx29, jsxs as jsxs13 } from "react/jsx-runtime";
var SelectItem = forwardRef17(({ hideSelectedIcon, className, children, ...props }, ref) => /* @__PURE__ */ jsxs13(
  SelectPrimitive6.Item,
  {
    ref,
    className: combineClasses27(
      "relative flex w-full cursor-pointer select-none items-center rounded-sm",
      "py-1.5 pl-2 pr-8 text-4xs leading-xs text-frost-900 outline-none",
      "data-[selected=true]:bg-primary/80 data-[selected=true]:text-white",
      "data-[state=checked]:bg-primary/80 data-[state=checked]:text-white",
      "hover:bg-primary/80 hover:text-white",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    ),
    ...props,
    children: [
      !hideSelectedIcon && /* @__PURE__ */ jsx29("span", { className: "absolute right-2 flex size-3.5 items-center justify-center", children: /* @__PURE__ */ jsx29(SelectPrimitive6.ItemIndicator, { children: /* @__PURE__ */ jsx29(CheckIcon2, { className: "size-4", "data-testid": "check-icon" }) }) }),
      /* @__PURE__ */ jsx29(SelectPrimitive6.ItemText, { children })
    ]
  }
));
SelectItem.displayName = SelectPrimitive6.Item.displayName;
var SelectItem_default = SelectItem;

// src/components/common/Select/base/SelectLabel.tsx
import { forwardRef as forwardRef18 } from "react";
import * as SelectPrimitive7 from "@radix-ui/react-select";
import { combineClasses as combineClasses28 } from "@shared/utils";
import { jsx as jsx30 } from "react/jsx-runtime";
var SelectLabel = forwardRef18(({ className, ...props }, ref) => /* @__PURE__ */ jsx30(
  SelectPrimitive7.Label,
  {
    ref,
    className: combineClasses28("px-2 py-1.5 text-sm font-semibold", className),
    ...props
  }
));
SelectLabel.displayName = SelectPrimitive7.Label.displayName;

// src/components/common/Select/base/SelectSeparator.tsx
import { forwardRef as forwardRef19 } from "react";
import * as SelectPrimitive8 from "@radix-ui/react-select";
import { combineClasses as combineClasses29 } from "@shared/utils";
import { jsx as jsx31 } from "react/jsx-runtime";
var SelectSeparator = forwardRef19(({ className, ...props }, ref) => /* @__PURE__ */ jsx31(
  SelectPrimitive8.Separator,
  {
    ref,
    className: combineClasses29("-mx-1 my-1 h-px bg-muted", className),
    ...props
  }
));
SelectSeparator.displayName = SelectPrimitive8.Separator.displayName;

// src/components/common/Select/base/SelectTrigger.tsx
import {
  forwardRef as forwardRef20
} from "react";
import * as SelectPrimitive9 from "@radix-ui/react-select";
import { combineClasses as combineClasses30 } from "@shared/utils";
import ChevronDownIcon3 from "@shared/icons/ChevronDownIcon";
import { jsx as jsx32, jsxs as jsxs14 } from "react/jsx-runtime";
var SelectTrigger = forwardRef20(
  ({ className, variant = "default", children, hideTriggerIcon, ...props }, ref) => /* @__PURE__ */ jsxs14(
    SelectPrimitive9.Trigger,
    {
      ref,
      className: combineClasses30(
        "flex h-[35px] w-full items-center justify-between whitespace-nowrap rounded-sm",
        "bg-background p-2 text-4xs leading-xs text-frost-900",
        "ring-1 ring-frost-900/20",
        "focus:outline-none disabled:cursor-not-allowed",
        "data-[placeholder]:text-frost-900/50",
        "data-[state=open]:ring-primary-500",
        "disabled:opacity-50 [&>span]:line-clamp-1",
        className
      ),
      ...props,
      children: [
        children,
        !hideTriggerIcon && /* @__PURE__ */ jsx32(SelectPrimitive9.Icon, { asChild: true, children: /* @__PURE__ */ jsx32(ChevronDownIcon3, { className: "size-5 stroke-slate-500 opacity-50" }) })
      ]
    }
  )
);
SelectTrigger.displayName = SelectPrimitive9.Trigger.displayName;
var SelectTrigger_default = SelectTrigger;

// src/components/common/Select/base/SelectValue.tsx
import {
  forwardRef as forwardRef21
} from "react";
import * as SelectPrimitive10 from "@radix-ui/react-select";
import { combineClasses as combineClasses31 } from "@shared/utils";
import { jsx as jsx33 } from "react/jsx-runtime";
var SelectValue = forwardRef21(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx33(
  SelectPrimitive10.Value,
  {
    ref,
    className: combineClasses31(className),
    ...props,
    children: /* @__PURE__ */ jsx33("div", { className: "flex items-center gap-2", children })
  }
));
SelectValue.displayName = SelectPrimitive10.Value.displayName;
var SelectValue_default = SelectValue;

// src/components/common/Select/index.tsx
import { combineClasses as combineClasses32 } from "@shared/utils";
import { jsx as jsx34, jsxs as jsxs15 } from "react/jsx-runtime";
var Select2 = ({
  options,
  value,
  disabled,
  placeholder = "Select",
  triggerClassName,
  valueClassName,
  contentClassName,
  itemClassName,
  variant,
  onChange,
  hideSelectedIcon,
  showIconAsValue = false,
  hideTriggerIcon = false,
  isInvalid = false
}) => {
  const selectedOption = options.find((option) => option.value === value);
  return /* @__PURE__ */ jsxs15(Select_default, { disabled, onValueChange: onChange, value, children: [
    /* @__PURE__ */ jsx34(
      SelectTrigger_default,
      {
        variant,
        "aria-label": "select-options",
        className: combineClasses32(
          "outline-none",
          "hover:ring-1 hover:ring-frost-900",
          "focus:ring-1 focus:ring-primary-500",
          triggerClassName,
          isInvalid && "ring-1 ring-red-500"
        ),
        hideTriggerIcon,
        children: /* @__PURE__ */ jsx34(
          SelectValue_default,
          {
            className: combineClasses32(valueClassName),
            placeholder,
            children: showIconAsValue && selectedOption?.icon ? selectedOption.icon : selectedOption?.label
          }
        )
      }
    ),
    /* @__PURE__ */ jsx34(SelectContent_default, { className: combineClasses32(contentClassName), children: options.map(({ value: value2, label, icon, disabled: optionDisabled }) => /* @__PURE__ */ jsx34(
      SelectItem_default,
      {
        value: value2 || "none",
        className: itemClassName,
        hideSelectedIcon,
        disabled: optionDisabled,
        children: icon ? /* @__PURE__ */ jsxs15("div", { className: "flex items-center gap-2", children: [
          icon,
          " ",
          label
        ] }) : label
      },
      value2
    )) })
  ] });
};
var Select_default2 = Select2;

// src/components/common/FabMenu/index.tsx
import { useEffect as useEffect3, useRef, useState as useState5 } from "react";
import { combineClasses as combineClasses33 } from "@shared/utils";
import PlusIcon from "@shared/icons/PlusIcon";
import XIcon from "@shared/icons/CloseIcon";
import { Fragment as Fragment2, jsx as jsx35, jsxs as jsxs16 } from "react/jsx-runtime";
var FabMenuItem = ({
  icon: Icon2,
  label,
  className,
  onClick
}) => {
  return /* @__PURE__ */ jsxs16(
    Button,
    {
      size: "icon",
      className: combineClasses33(
        "rounded-full shadow-md transition-all duration-200 h-12 w-12 scale-up-center",
        className
      ),
      onClick,
      "aria-label": label,
      children: [
        /* @__PURE__ */ jsx35(Icon2, { className: "h-5 w-5" }),
        label && /* @__PURE__ */ jsx35("span", { className: "sr-only", children: label })
      ]
    }
  );
};
var FabMenu = ({
  items,
  className,
  icon: Icon2 = PlusIcon,
  closeIcon: CloseIcon3 = XIcon,
  label,
  showLabels = false,
  showOverlay = true,
  overlayClassName
}) => {
  const [isOpen, setIsOpen] = useState5(false);
  const menuRef = useRef(null);
  useEffect3(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const handleClickMenuButton = () => {
    setIsOpen((prev) => !prev);
  };
  return /* @__PURE__ */ jsxs16(Fragment2, { children: [
    isOpen && showOverlay && /* @__PURE__ */ jsx35(
      "div",
      {
        className: combineClasses33(
          "fixed z-40 inset-0 bg-black/25 transition-opacity duration-200",
          overlayClassName
        ),
        "aria-hidden": "true"
      }
    ),
    /* @__PURE__ */ jsxs16(
      "div",
      {
        ref: menuRef,
        className: combineClasses33(
          "flex flex-col items-end gap-4 relative z-50",
          className
        ),
        children: [
          isOpen && /* @__PURE__ */ jsx35("div", { className: "flex flex-col gap-3 items-end mr-1", children: items.map(({ onClick, label: label2, ...rest }, index) => {
            const handleClickMenuItem = () => {
              setIsOpen(false);
              onClick?.();
            };
            return /* @__PURE__ */ jsxs16("div", { className: "flex items-center gap-2", children: [
              showLabels && label2 && /* @__PURE__ */ jsx35("span", { className: "scale-up-center text-sm font-medium py-1 px-3 rounded-sm w-max bg-white shadow-md", children: label2 }),
              /* @__PURE__ */ jsx35(
                FabMenuItem,
                {
                  onClick: handleClickMenuItem,
                  label: label2,
                  ...rest
                }
              )
            ] }, index);
          }) }),
          /* @__PURE__ */ jsxs16("div", { className: "flex items-center gap-2", children: [
            label && /* @__PURE__ */ jsx35("span", { className: "text-sm font-medium py-1 px-3", children: label }),
            /* @__PURE__ */ jsx35(
              Button,
              {
                size: "icon",
                variant: "default",
                className: "rounded-full h-14 w-14 shadow-lg relative overflow-hidden",
                onClick: handleClickMenuButton,
                "aria-label": isOpen ? "Close menu" : "Open menu",
                children: /* @__PURE__ */ jsxs16("div", { className: "relative w-full h-full flex items-center justify-center", children: [
                  /* @__PURE__ */ jsx35(
                    CloseIcon3,
                    {
                      className: combineClasses33(
                        "h-6 w-6 absolute transition-all duration-300 ease-in-out",
                        isOpen ? "opacity-100 rotate-0 scale-100" : "opacity-0 rotate-90 scale-75"
                      )
                    }
                  ),
                  /* @__PURE__ */ jsx35(
                    Icon2,
                    {
                      className: combineClasses33(
                        "h-6 w-6 absolute transition-all duration-300 ease-in-out",
                        isOpen ? "opacity-0 rotate-90 scale-75" : "opacity-100 rotate-0 scale-100"
                      )
                    }
                  )
                ] })
              }
            )
          ] })
        ]
      }
    )
  ] });
};
var FabMenu_default = FabMenu;

// src/components/common/AutoComplete/index.tsx
import { Command as CommandPrimitive2 } from "cmdk";
import CloseIcon2 from "@shared/icons/CloseIcon";
import CloseSquareIcon from "@shared/icons/CloseSquareIcon";
import ChevronUpCircleIcon from "@shared/icons/ChevronUpCircleIcon";
import ChevronDownCircleIcon from "@shared/icons/ChevronDownCircleIcon";
import {
  useState as useState6,
  useRef as useRef2,
  useCallback,
  useEffect as useEffect4,
  useMemo
} from "react";
import { createPortal } from "react-dom";
import { combineClasses as combineClasses34, isArray, isEqual, normalizeText } from "@shared/utils";
import { useDebounce, useDropdownPosition } from "@shared/hooks";
import { Fragment as Fragment3, jsx as jsx36, jsxs as jsxs17 } from "react/jsx-runtime";
var AutoComplete = ({
  options,
  placeholder,
  emptyMessage,
  value,
  onValueChange,
  disabled,
  isError,
  onFocus,
  onBlur,
  dropDownMaxHeight = 250,
  isLoading = false,
  multiSelect = false,
  hasCategory = false,
  allowFreeText = false,
  addNewLabel = "Add",
  renderCustomOption,
  customInputClassName,
  asyncSearch,
  showListOnlyOnInput = false
}) => {
  const inputRef = useRef2(null);
  const listRef = useRef2(null);
  const isEditingRef = useRef2(false);
  const prevValueRef = useRef2(value);
  const [isOpen, setOpen] = useState6(false);
  const { coords, maxHeight, wrapperRef } = useDropdownPosition({
    isOpen,
    dropdownHeight: dropDownMaxHeight
  });
  const valueSelectedItems = useMemo(() => {
    if (multiSelect && isArray(value)) {
      return value;
    } else if (!multiSelect && value && !isArray(value)) {
      return [value];
    }
    return [];
  }, [multiSelect, value]);
  const labelValueSelect = useMemo(
    () => !multiSelect && value && !isArray(value) ? value.label : "",
    [multiSelect, value]
  );
  const [selectedItems, setSelectedItems] = useState6(valueSelectedItems);
  const [inputValue, setInputValue] = useState6(labelValueSelect);
  const [duplicateItem, setDuplicateItem] = useState6(null);
  const [asyncOptions, setAsyncOptions] = useState6([]);
  const debouncedKeyword = useDebounce(inputValue, 300);
  useEffect4(() => {
    if (duplicateItem) {
      const timer = setTimeout(() => {
        setDuplicateItem(null);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [duplicateItem]);
  useEffect4(() => {
    const valueChanged = !isEqual(prevValueRef.current, value);
    if (valueChanged || !isEditingRef.current) {
      setInputValue(labelValueSelect);
      setSelectedItems(valueSelectedItems);
      prevValueRef.current = value;
    }
  }, [labelValueSelect, valueSelectedItems, value]);
  const checkIsSelectOption = useCallback(
    (selectedOption) => selectedItems.some((item) => item.value === selectedOption.value),
    [selectedItems]
  );
  const handleMouseDownCommandItem = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  const handleClearAll = useCallback(
    (event) => {
      if (disabled) return;
      event.stopPropagation();
      event.preventDefault();
      setSelectedItems([]);
      setInputValue("");
      if (multiSelect) {
        onValueChange?.([]);
      } else {
        onValueChange?.(void 0);
      }
      setTimeout(() => {
        inputRef?.current?.focus();
      }, 0);
    },
    [disabled, multiSelect, onValueChange]
  );
  const handleClickAutoCompleteWrapper = () => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  };
  const handleSelectOption = useCallback(
    (selectedOption) => {
      let newSelectedItems;
      if (multiSelect) {
        const duplicate = selectedItems.find(
          (item) => item.value === selectedOption.value || normalizeText(item.label, { toLowerCase: true }) === normalizeText(selectedOption.label, { toLowerCase: true })
        );
        if (duplicate) {
          setDuplicateItem(duplicate);
          return;
        }
        newSelectedItems = [...selectedItems, selectedOption];
        setSelectedItems(newSelectedItems);
        setInputValue("");
        if (showListOnlyOnInput) {
          setOpen(false);
        }
        setTimeout(() => {
          inputRef?.current?.focus();
        }, 0);
      } else {
        newSelectedItems = [selectedOption];
        setSelectedItems(newSelectedItems);
        setInputValue(selectedOption.label);
        setTimeout(() => {
          inputRef?.current?.blur();
        }, 0);
      }
      if (multiSelect) {
        onValueChange?.(
          newSelectedItems
        );
      } else {
        onValueChange?.(
          newSelectedItems[0]
        );
      }
    },
    [selectedItems, multiSelect, onValueChange, showListOnlyOnInput]
  );
  const addCustomOption = useCallback(
    (value2) => {
      const newOption = {
        value: value2,
        label: normalizeText(value2, { trim: true }),
        isCustom: true
      };
      handleSelectOption(newOption);
    },
    [handleSelectOption]
  );
  const handleKeyDown = useCallback(
    (event) => {
      const input = inputRef.current;
      if (!input || disabled) {
        return;
      }
      if (!isOpen) {
        setOpen(true);
      }
      if (event.key === "Enter" && input.value !== "") {
        const optionToSelect = options.find(
          (option) => normalizeText(option.label, { toLowerCase: true }) === normalizeText(input.value, { toLowerCase: true })
        );
        if (optionToSelect) {
          handleSelectOption(optionToSelect);
        } else if (allowFreeText && normalizeText(input.value, { trim: true })) {
          addCustomOption(input.value);
        }
      }
      if (event.key === "Escape") {
        input.blur();
      }
      if (event.key === "Backspace" && inputValue === "" && multiSelect && selectedItems.length > 0) {
        const newSelectedItems = [...selectedItems];
        newSelectedItems.pop();
        setSelectedItems(newSelectedItems);
        if (multiSelect) {
          onValueChange?.(
            newSelectedItems
          );
        } else if (newSelectedItems.length > 0) {
          onValueChange?.(
            newSelectedItems[0]
          );
        } else {
          onValueChange?.(void 0);
        }
      }
    },
    [
      disabled,
      isOpen,
      options,
      multiSelect,
      inputValue,
      selectedItems,
      onValueChange,
      handleSelectOption,
      allowFreeText,
      addCustomOption
    ]
  );
  const handleBlur = useCallback(() => {
    onBlur?.();
    setOpen(false);
    isEditingRef.current = false;
    if (!multiSelect && selectedItems.length > 0) {
      setInputValue(selectedItems[0]?.label || "");
    } else {
      setInputValue("");
    }
  }, [multiSelect, selectedItems, onBlur]);
  const removeItem = useCallback(
    (optionToRemove, event) => {
      event.stopPropagation();
      event.preventDefault();
      const newSelectedItems = selectedItems.filter(
        (item) => item.value !== optionToRemove.value
      );
      setSelectedItems(newSelectedItems);
      if (multiSelect) {
        onValueChange?.(
          newSelectedItems
        );
      } else if (newSelectedItems.length > 0) {
        onValueChange?.(
          newSelectedItems[0]
        );
      }
      setTimeout(() => {
        inputRef?.current?.focus();
      }, 0);
    },
    [selectedItems, multiSelect, onValueChange]
  );
  const handleFocusInputWrapper = useCallback(
    (event) => {
      if (!disabled && (event.key === "Enter" || event.key === " ")) {
        inputRef.current?.focus();
      }
    },
    [disabled]
  );
  useEffect4(() => {
    if (!asyncSearch) return;
    const keyword = normalizeText(debouncedKeyword, {
      trim: true,
      toLowerCase: true
    });
    if (keyword.length === 0) {
      setAsyncOptions([]);
      return;
    }
    const fetched = asyncSearch(keyword);
    setAsyncOptions(fetched);
  }, [debouncedKeyword, asyncSearch]);
  useEffect4(() => {
    if (isOpen && listRef.current) {
      const timeoutId = setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollTop = 0;
        }
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);
  const handleFocusInput = useCallback(() => {
    if (!disabled) {
      if (!showListOnlyOnInput) {
        setOpen(true);
      }
      isEditingRef.current = true;
      onFocus?.();
    }
  }, [disabled, onFocus, showListOnlyOnInput]);
  const handleInputChange = (newValue) => {
    isEditingRef.current = true;
    setInputValue(newValue);
    if (showListOnlyOnInput) {
      const hasInput = newValue.trim().length > 0;
      setOpen(hasInput);
    }
  };
  const showPlaceholder = !multiSelect || multiSelect && selectedItems.length === 0;
  const renderOptions = () => {
    const finalOptions = [
      ...options,
      ...asyncOptions.filter((a) => !options.some((o) => o.value === a.value))
    ];
    if (!hasCategory) {
      return /* @__PURE__ */ jsx36(CommandGroup, { children: finalOptions.map((option, index) => {
        const isSelected = checkIsSelectOption(option);
        return /* @__PURE__ */ jsx36(
          CommandItem,
          {
            value: `${option.label} ${option.description || ""}`,
            onMouseDown: handleMouseDownCommandItem,
            onSelect: () => handleSelectOption(option),
            className: combineClasses34("flex w-full items-center gap-2", {
              "pl-2": !isSelected
            }),
            children: renderCustomOption ? renderCustomOption(option, isSelected) : option.label
          },
          `${option.value}-${index}`
        );
      }) });
    }
    const groupedOptions = options.reduce(
      (groups, option) => {
        const category = option.category || "Other";
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(option);
        return groups;
      },
      {}
    );
    return Object.entries(groupedOptions).map(([category, categoryOptions]) => /* @__PURE__ */ jsx36(CommandGroup, { heading: category, children: categoryOptions.map((option, index) => {
      const isSelected = checkIsSelectOption(option);
      return /* @__PURE__ */ jsx36(
        CommandItem,
        {
          value: `${option.label} ${option.description || ""}`,
          onMouseDown: handleMouseDownCommandItem,
          onSelect: () => handleSelectOption(option),
          className: combineClasses34("flex w-full items-center gap-2", {
            "pl-2": !isSelected
          }),
          children: renderCustomOption ? renderCustomOption(option, isSelected) : option.label
        },
        `${category}-${option.value}-${index}`
      );
    }) }, category));
  };
  return /* @__PURE__ */ jsx36(CommandPrimitive2, { onKeyDown: handleKeyDown, children: /* @__PURE__ */ jsxs17("div", { className: "mt-1", ref: wrapperRef, children: [
    /* @__PURE__ */ jsxs17(
      "div",
      {
        role: "combobox",
        "data-testid": "autocomplete-wrapper",
        "aria-controls": "autocomplete-list",
        "aria-expanded": isOpen,
        tabIndex: 0,
        className: combineClasses34(
          "flex flex-wrap items-center gap-1 w-full rounded-md",
          "pl-3 pr-12.25 text-4xs leading-xs",
          "hover:ring-1 hover:ring-frost-900",
          "focus-within:ring-1 focus-within:ring-primary-500",
          "relative bg-frost-100",
          isError && "ring-1 ring-destructive",
          isOpen && "ring-1 ring-primary-500 hover:ring-primary-500",
          multiSelect && selectedItems.length > 0 && "py-4",
          disabled && "cursor-not-allowed ring-0 hover:ring-0 focus-within:ring-0"
        ),
        onClick: handleClickAutoCompleteWrapper,
        onKeyDown: handleFocusInputWrapper,
        children: [
          multiSelect && selectedItems.length > 0 && /* @__PURE__ */ jsx36(Fragment3, { children: selectedItems.map((item) => /* @__PURE__ */ jsx36(
            Badge,
            {
              className: combineClasses34({
                "bg-destructive text-destructive-foreground animate-flash-error": duplicateItem?.value === item.value
              }),
              label: item.description ?? item.label,
              onDelete: (event) => removeItem(item, event),
              deleteIcon: /* @__PURE__ */ jsx36(CloseIcon2, { className: "size-3 stroke-frost-300" }),
              isDisabled: disabled,
              avatar: item.avatar,
              avatarClassName: "w-6 h-6 rounded-full"
            },
            item.value
          )) }),
          /* @__PURE__ */ jsx36(
            CommandInput,
            {
              ref: inputRef,
              value: inputValue,
              onValueChange: handleInputChange,
              onBlur: handleBlur,
              onFocus: handleFocusInput,
              "data-testid": "autocomplete-input",
              placeholder: showPlaceholder ? placeholder : "",
              disabled,
              className: combineClasses34(
                "flex-1 bg-transparent py-0 pl-0 outline-none border-none focus:ring-0 text-4xs w-full",
                customInputClassName
              )
            }
          ),
          /* @__PURE__ */ jsxs17("div", { className: "absolute top-0 right-0 h-full flex items-center gap-1 pr-3", children: [
            multiSelect && selectedItems.length === 0 && /* @__PURE__ */ jsx36(
              "button",
              {
                type: "button",
                onClick: handleFocusInput,
                disabled,
                className: "select-icon-button",
                title: isOpen ? "Collapse dropdown" : "Open dropdown",
                "aria-label": "Toggle dropdown",
                "data-testid": "multi-toggle-icon",
                children: isOpen ? /* @__PURE__ */ jsx36(ChevronUpCircleIcon, { className: "clear-icon-button" }) : /* @__PURE__ */ jsx36(ChevronDownCircleIcon, { className: "clear-icon-button" })
              }
            ),
            multiSelect && selectedItems.length > 0 && /* @__PURE__ */ jsx36(
              "button",
              {
                type: "button",
                onClick: handleClearAll,
                disabled,
                className: "select-icon-button",
                title: "Clear all",
                "aria-label": "Clear all selected items",
                "data-testid": "clear-all-button",
                children: /* @__PURE__ */ jsx36(CloseSquareIcon, { className: "clear-icon-button" })
              }
            ),
            !multiSelect && selectedItems.length > 0 && /* @__PURE__ */ jsx36(
              "button",
              {
                type: "button",
                onClick: handleClearAll,
                disabled,
                className: "select-icon-button",
                title: "Clear",
                "aria-label": "Clear selected item",
                "data-testid": "single-clear-button",
                children: /* @__PURE__ */ jsx36(CloseSquareIcon, { className: "clear-icon-button" })
              }
            ),
            !multiSelect && /* @__PURE__ */ jsx36(
              "button",
              {
                type: "button",
                onClick: handleFocusInput,
                disabled,
                className: "select-icon-button",
                title: isOpen ? "Collapse dropdown" : "Open dropdown",
                "aria-label": "Toggle dropdown",
                "data-testid": "collapse-button",
                children: isOpen ? /* @__PURE__ */ jsx36(ChevronUpCircleIcon, { className: "clear-icon-button" }) : /* @__PURE__ */ jsx36(ChevronDownCircleIcon, { className: "clear-icon-button" })
              }
            )
          ] })
        ]
      }
    ),
    createPortal(
      /* @__PURE__ */ jsx36(
        "div",
        {
          className: combineClasses34(
            "rounded-md bg-white",
            isOpen && coords && "animate-in fade-in-0 slide-in-from-top-1"
          ),
          id: "autocomplete-list",
          style: isOpen && coords ? {
            position: "fixed",
            top: coords.top,
            bottom: coords.bottom,
            left: coords.left,
            width: coords.width,
            zIndex: 50
          } : { display: "none" },
          children: /* @__PURE__ */ jsxs17(
            CommandList,
            {
              ref: listRef,
              className: combineClasses34(
                "rounded-lg ring-1 ring-slate-200",
                "overflow-auto small-scrollbar"
              ),
              style: { maxHeight: `${maxHeight}px` },
              children: [
                isLoading && /* @__PURE__ */ jsx36(CommandPrimitive2.Loading, { children: /* @__PURE__ */ jsxs17("div", { className: "p-2 flex flex-col gap-2", children: [
                  /* @__PURE__ */ jsx36(Skeleton_default, { className: "h-8 w-full" }),
                  /* @__PURE__ */ jsx36(Skeleton_default, { className: "h-8 w-full" }),
                  /* @__PURE__ */ jsx36(Skeleton_default, { className: "h-8 w-full" })
                ] }) }),
                (options.length > 0 || asyncOptions.length > 0) && !isLoading && renderOptions(),
                allowFreeText && normalizeText(inputValue, { trim: true }) && !isLoading && !options.find(
                  (option) => normalizeText(option.label, { toLowerCase: true }) === normalizeText(inputValue, { toLowerCase: true })
                ) && /* @__PURE__ */ jsx36(CommandGroup, { children: /* @__PURE__ */ jsx36(
                  CommandItem,
                  {
                    value: inputValue,
                    onSelect: () => addCustomOption(inputValue),
                    onMouseDown: handleMouseDownCommandItem,
                    className: "flex w-full items-center gap-2",
                    children: `${addNewLabel} "${normalizeText(inputValue, { trim: true })}"`
                  }
                ) }),
                !isLoading && /* @__PURE__ */ jsx36(CommandPrimitive2.Empty, { className: "select-none rounded-sm px-2 py-3 text-center text-3xs text-slate-900", children: emptyMessage })
              ]
            }
          )
        }
      ),
      document.body
    )
  ] }) });
};
var AutoComplete_default = AutoComplete;

// src/components/common/Toaster/index.tsx
import { useTheme as useTheme2 } from "next-themes";
import { Toaster as Sonner } from "sonner";
import { jsx as jsx37 } from "react/jsx-runtime";
var Toaster = ({ ...props }) => {
  const { theme = "light" } = useTheme2();
  return /* @__PURE__ */ jsx37(
    Sonner,
    {
      theme,
      className: "toaster group",
      toastOptions: {
        classNames: {
          toast: "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground"
        }
      },
      ...props
    }
  );
};
var Toaster_default = Toaster;

// src/components/common/ImageCustom/index.tsx
import { memo as memo2, useEffect as useEffect5, useState as useState7 } from "react";
import Image2 from "next/image";
import { BLUR_SRC as BLUR_SRC2, FALLBACK_SRC as FALLBACK_SRC2 } from "@shared/constants";
import { jsx as jsx38, jsxs as jsxs18 } from "react/jsx-runtime";
var ImageCustom = ({
  blurDataURL = BLUR_SRC2.DEFAULT,
  fallbackSrc = FALLBACK_SRC2.DEFAULT,
  placeholder,
  src,
  srcSet,
  srcSetMobile,
  alt,
  width,
  height,
  classLayout,
  ...rest
}) => {
  const [imgSrc, setImgSrc] = useState7(() => {
    if (src instanceof Blob) {
      return URL.createObjectURL(src);
    }
    return src;
  });
  const [objectUrl, setObjectUrl] = useState7(null);
  const shouldUseBlur = placeholder === "blur" && blurDataURL;
  useEffect5(() => {
    let newSrc;
    if (src instanceof Blob) {
      const blobUrl = URL.createObjectURL(src);
      setObjectUrl(blobUrl);
      newSrc = blobUrl;
    } else {
      newSrc = src;
    }
    setImgSrc(newSrc);
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        setObjectUrl(null);
      }
    };
  }, [src, objectUrl]);
  const handleFallbackImage = () => {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      setObjectUrl(null);
    }
    setImgSrc(fallbackSrc);
  };
  const handleLoad = (event) => {
    if (src instanceof Blob) return;
    const img = event.currentTarget;
    if (img.naturalWidth === 0 || img.naturalHeight === 0 || !img.complete) {
      handleFallbackImage();
      return;
    }
    if (objectUrl && imgSrc === objectUrl) {
      try {
        if (img.width === 0 || img.height === 0) {
          handleFallbackImage();
        }
      } catch (error) {
        console.error(error);
        handleFallbackImage();
      }
    }
  };
  return /* @__PURE__ */ jsxs18("picture", { className: classLayout, children: [
    /* @__PURE__ */ jsx38("source", { media: "(min-width:641px)", srcSet, type: "image/webp" }),
    /* @__PURE__ */ jsx38(
      "source",
      {
        media: "(max-width:640px)",
        srcSet: srcSetMobile,
        type: "image/webp"
      }
    ),
    /* @__PURE__ */ jsx38(
      Image2,
      {
        priority: true,
        src: imgSrc,
        alt,
        placeholder: shouldUseBlur ? "blur" : void 0,
        blurDataURL: shouldUseBlur ? blurDataURL : void 0,
        width,
        height,
        onLoad: handleLoad,
        onError: handleFallbackImage,
        quality: 100,
        sizes: "(min-width: 768px) 50vw, 100vw",
        ...rest
      }
    )
  ] });
};
var ImageCustom_default = memo2(ImageCustom);

// src/components/common/Checkbox/index.tsx
import { forwardRef as forwardRef23 } from "react";
import { combineClasses as combineClasses36 } from "@shared/utils";

// src/components/common/Checkbox/base/index.tsx
import { forwardRef as forwardRef22 } from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import CheckIcon3 from "@shared/icons/CheckIcon";
import { combineClasses as combineClasses35 } from "@shared/utils";
import { jsx as jsx39 } from "react/jsx-runtime";
var CheckboxBase = forwardRef22(({ className, ...props }, ref) => /* @__PURE__ */ jsx39(
  CheckboxPrimitive.Root,
  {
    ref,
    className: combineClasses35(
      "peer h-5 w-5 shrink-0 rounded-sm border-2 border-white",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "data-[state=checked]:bg-primary-700 data-[state=checked]:text-success-foreground",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx39(
      CheckboxPrimitive.Indicator,
      {
        className: combineClasses35("flex-center text-current"),
        children: /* @__PURE__ */ jsx39(CheckIcon3, { className: "size-3" })
      }
    )
  }
));
CheckboxBase.displayName = CheckboxPrimitive.Root.displayName;

// src/components/common/Checkbox/index.tsx
import { jsx as jsx40, jsxs as jsxs19 } from "react/jsx-runtime";
var Checkbox = forwardRef23(
  ({
    label,
    disabled,
    className,
    labelClassName,
    labelPosition = "right",
    onCheckedChange,
    ...props
  }, ref) => {
    const handleChange = (checked) => {
      if (!disabled && onCheckedChange) {
        onCheckedChange(checked);
      }
    };
    return /* @__PURE__ */ jsxs19(
      "label",
      {
        className: combineClasses36(
          "flex-center gap-2.5",
          labelPosition === "left" && "flex-row-reverse",
          disabled ? "cursor-not-allowed" : "cursor-pointer"
        ),
        children: [
          /* @__PURE__ */ jsx40("div", { className: "w-8 h-8 bg-frost-100 flex-center rounded-2sm", children: /* @__PURE__ */ jsx40(
            CheckboxBase,
            {
              ref,
              disabled,
              onCheckedChange: handleChange,
              className,
              ...props
            }
          ) }),
          label && /* @__PURE__ */ jsx40(
            "span",
            {
              className: combineClasses36(
                "text-3xs font-normal",
                "[&]:text-slate-800/90",
                disabled && "opacity-50",
                labelClassName
              ),
              children: label
            }
          )
        ]
      }
    );
  }
);
Checkbox.displayName = "Checkbox";
var Checkbox_default = Checkbox;

// src/components/common/LoadingIndicator/index.tsx
import { combineClasses as combineClasses37 } from "@shared/utils";
import LoadingIcon from "@shared/icons/LoadingIcon";
import { jsx as jsx41, jsxs as jsxs20 } from "react/jsx-runtime";
var LOADING_SIZES = {
  sm: "w-4 h-4",
  default: "w-6 h-6",
  lg: "w-8 h-8",
  xl: "w-12 h-12"
};
var LoadingIndicator = ({
  size = "default",
  center = false,
  color,
  text,
  className,
  iconClassName,
  textClassName,
  ...props
}) => {
  return /* @__PURE__ */ jsxs20(
    "div",
    {
      role: "status",
      "aria-label": "Loading",
      "data-testid": "loading-indicator",
      className: combineClasses37(
        "flex flex-col items-center z-50",
        center && "justify-center",
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx41(
          LoadingIcon,
          {
            "data-testid": "loading-icon",
            className: combineClasses37(
              LOADING_SIZES[size],
              color && `text-${color}`,
              iconClassName
            )
          }
        ),
        text && /* @__PURE__ */ jsx41(
          "span",
          {
            className: combineClasses37(
              "mt-2 text-sm text-muted-foreground",
              textClassName
            ),
            children: text
          }
        )
      ]
    }
  );
};
LoadingIndicator.displayName = "LoadingIndicator";
var LoadingIndicator_default = LoadingIndicator;

// src/components/common/AddressAutocomplete/index.tsx
import {
  useEffect as useEffect6,
  useState as useState8,
  useCallback as useCallback2,
  useRef as useRef3
} from "react";
import {
  useDebounce as useDebounce2,
  useClickOutside,
  useScrollSuggestionIntoView
} from "@shared/hooks";
import { LocationIcon } from "@shared/icons/LocationIcon";
import { KEYBOARD_EVENT, MESSAGES } from "@shared/constants";
import ENV from "@shared/configs";
import {
  combineClasses as combineClasses38,
  normalizeSearchValue,
  formatAddressSuggestions,
  getAddressAutocompleteIcon,
  isSearchTermValid,
  getFocusPosition
} from "@shared/utils";
import CloseSquareIcon2 from "@shared/icons/CloseSquareIcon";
import { jsx as jsx42, jsxs as jsxs21 } from "react/jsx-runtime";
var STYLES = {
  SUGGESTION_ITEM: "px-[8.5px] py-2 text-4xs leading-xs text-black",
  SPINNER: "animate-spin size-4 border-2 border-frost-500 border-t-transparent rounded-full",
  SUGGESTION_CONTAINER: combineClasses38(
    "absolute z-20 w-full mt-1",
    "max-h-60 overflow-auto tiny-scrollbar transition-all duration-200",
    "bg-white border border-frost-300 rounded-md shadow-lg"
  ),
  CLEAR_BUTTON: combineClasses38(
    "absolute right-3 top-1/2 -translate-y-1/2",
    "size-4 transition-colors cursor-pointer",
    "disabled:cursor-not-allowed"
  )
};
var AddressAutocomplete = ({
  value,
  placeholder = MESSAGES.ADDRESS_AUTOCOMPLETE_PLACEHOLDER,
  isInvalid = false,
  className = "",
  disabled = false,
  addressSuggestions = [],
  isLoading = false,
  onFocus,
  onBlur,
  onInputChange,
  onSelect,
  onSearchQueryChange
}) => {
  const [isOpen, setIsOpen] = useState8(false);
  const [inputValue, setInputValue] = useState8(value);
  const [focusedIndex, setFocusedIndex] = useState8(-1);
  const [searchTerm, setSearchTerm] = useState8("");
  const [isUserTyping, setIsUserTyping] = useState8(false);
  const containerRef = useRef3(null);
  const suggestionsRef = useRef3(null);
  const currentInputValueRef = useRef3(value);
  const inputRef = useRef3(null);
  const normalizedSearchValue = normalizeSearchValue(inputValue);
  const debouncedSearch = useDebounce2(
    normalizedSearchValue,
    ENV.MAPBOX_DEBOUNCE_DELAY
  );
  useClickOutside(() => setIsOpen(false), containerRef);
  useScrollSuggestionIntoView(focusedIndex, suggestionsRef);
  const updateInputValue = useCallback2((newValue) => {
    setInputValue(newValue);
    currentInputValueRef.current = newValue;
  }, []);
  useEffect6(() => {
    if (debouncedSearch !== searchTerm) {
      setSearchTerm(debouncedSearch);
      if (onSearchQueryChange && isSearchTermValid(debouncedSearch, searchTerm)) {
        onSearchQueryChange(debouncedSearch);
      }
    }
  }, [debouncedSearch, searchTerm, onSearchQueryChange]);
  useEffect6(() => {
    if (value !== currentInputValueRef.current) {
      updateInputValue(value);
    }
  }, [value, updateInputValue]);
  const handleInputChange = useCallback2(
    (e) => {
      const newValue = e.target.value;
      updateInputValue(newValue);
      onInputChange(newValue);
      setIsUserTyping(true);
      setIsOpen(true);
      setFocusedIndex(-1);
    },
    [onInputChange, updateInputValue]
  );
  const handleFocus = useCallback2(() => {
    if (inputValue.trim() === "") {
      setIsOpen(true);
    }
    onFocus?.();
  }, [inputValue, onFocus]);
  const handleInputBlur = useCallback2(() => {
    setIsOpen(false);
    setIsUserTyping(false);
    onBlur?.();
    if (inputValue.trim() === "") {
      onInputChange("");
      onSelect?.(null);
    }
  }, [inputValue, onInputChange, onSelect]);
  const handleClearInput = useCallback2(() => {
    updateInputValue("");
    onInputChange("");
    onSelect?.(null);
    setIsOpen(false);
    setFocusedIndex(-1);
    setIsUserTyping(false);
    setSearchTerm("");
  }, [onInputChange, onSelect, updateInputValue]);
  const handleSuggestionClick = useCallback2(
    (suggestion) => {
      const formattedAddress = formatAddressSuggestions(suggestion);
      updateInputValue(formattedAddress);
      onInputChange(formattedAddress);
      onSelect?.(suggestion);
      setIsOpen(false);
      setFocusedIndex(-1);
      setIsUserTyping(false);
    },
    [onInputChange, onSelect, updateInputValue]
  );
  const handleKeyDown = useCallback2(
    (e) => {
      if (!isOpen) return;
      switch (e.key) {
        case KEYBOARD_EVENT.ARROW_DOWN:
        case KEYBOARD_EVENT.TAB:
          e.preventDefault();
          setFocusedIndex(
            getFocusPosition(focusedIndex, addressSuggestions.length, true)
          );
          break;
        case KEYBOARD_EVENT.ARROW_UP:
          e.preventDefault();
          setFocusedIndex(
            getFocusPosition(focusedIndex, addressSuggestions.length, false)
          );
          break;
        case KEYBOARD_EVENT.ENTER:
          e.preventDefault();
          if (focusedIndex >= 0 && addressSuggestions[focusedIndex]) {
            handleSuggestionClick(addressSuggestions[focusedIndex]);
          }
          break;
        case KEYBOARD_EVENT.ESCAPE:
          e.preventDefault();
          setIsOpen(false);
          setFocusedIndex(-1);
          break;
        default:
          break;
      }
    },
    [isOpen, addressSuggestions, focusedIndex, handleSuggestionClick]
  );
  const handleSuggestionItemClick = useCallback2(
    (suggestion) => (e) => {
      e.preventDefault();
      handleSuggestionClick(suggestion);
    },
    [handleSuggestionClick]
  );
  const handleMouseEnter = useCallback2(
    (index) => () => {
      setFocusedIndex(index);
    },
    []
  );
  const renderSuggestionItem = (suggestion, index) => {
    const {
      id = "",
      text = "",
      place_name = "",
      feature_type = ""
    } = suggestion;
    return /* @__PURE__ */ jsx42("li", { className: "w-full", children: /* @__PURE__ */ jsx42(
      "button",
      {
        type: "button",
        role: "option",
        "aria-selected": focusedIndex === index,
        onMouseDown: handleSuggestionItemClick(suggestion),
        onMouseEnter: handleMouseEnter(index),
        className: combineClasses38(
          "w-full px-4 py-2 text-3xs lg:text-xs text-left",
          "hover:bg-frost-200 cursor-pointer transition-colors duration-150",
          focusedIndex === index && "bg-frost-200"
        ),
        children: /* @__PURE__ */ jsxs21("div", { className: "flex items-start", children: [
          feature_type && /* @__PURE__ */ jsx42("span", { className: "mr-2 text-frost-500 mt-1 flex-shrink-0", children: getAddressAutocompleteIcon(feature_type) }),
          /* @__PURE__ */ jsxs21("div", { className: "flex flex-col", children: [
            text && /* @__PURE__ */ jsx42("span", { className: "font-medium text-2xs leading-xs text-black", children: text }),
            place_name && /* @__PURE__ */ jsx42("span", { className: "text-4xs leading-xs text-frost-900 mt-0.5", children: place_name })
          ] })
        ] })
      }
    ) }, id);
  };
  const renderAddressSuggestions = () => {
    if (isLoading) {
      return /* @__PURE__ */ jsx42("li", { className: STYLES.SUGGESTION_ITEM, children: /* @__PURE__ */ jsxs21("div", { className: "flex items-center", children: [
        /* @__PURE__ */ jsx42("div", { className: "mr-2", children: /* @__PURE__ */ jsx42("div", { className: STYLES.SPINNER }) }),
        MESSAGES.ADDRESS_AUTOCOMPLETE_SEARCHING
      ] }) }, "loading");
    }
    if (!addressSuggestions.length) {
      const normalizedSearchTerm = normalizeSearchValue(searchTerm);
      return normalizedSearchTerm ? /* @__PURE__ */ jsx42("li", { className: STYLES.SUGGESTION_ITEM, children: MESSAGES.ADDRESS_AUTOCOMPLETE_NO_RESULTS }, "no-results") : /* @__PURE__ */ jsx42("li", { className: STYLES.SUGGESTION_ITEM, children: MESSAGES.ADDRESS_AUTOCOMPLETE_TYPE_TO_SEARCH }, "empty");
    }
    return addressSuggestions.map(
      (suggestion, index) => renderSuggestionItem(suggestion, index)
    );
  };
  return /* @__PURE__ */ jsxs21("div", { ref: containerRef, className: combineClasses38("relative", className), children: [
    /* @__PURE__ */ jsxs21("div", { className: "relative", children: [
      /* @__PURE__ */ jsx42(LocationIcon, { className: "text-frost-300 absolute left-2 top-1/2 -translate-y-1/2 text-frost-500 z-10" }),
      /* @__PURE__ */ jsx42(
        Input,
        {
          ref: inputRef,
          value: inputValue,
          placeholder,
          isInvalid,
          className: combineClasses38("px-7.5", className),
          disabled,
          autoComplete: "off",
          "aria-autocomplete": "list",
          "aria-controls": "address-suggestions",
          "aria-expanded": isOpen && isUserTyping,
          role: "combobox",
          "aria-haspopup": "listbox",
          onChange: handleInputChange,
          onFocus: handleFocus,
          onBlur: handleInputBlur,
          onKeyDown: handleKeyDown
        }
      ),
      isLoading && /* @__PURE__ */ jsx42("div", { className: "absolute right-3 top-1/2 -translate-y-1/2 z-10", children: /* @__PURE__ */ jsx42("div", { className: STYLES.SPINNER }) }),
      inputValue && !isLoading && /* @__PURE__ */ jsx42(
        "button",
        {
          "data-testid": "clear-address-button",
          "aria-label": "Clear address",
          title: "Clear address",
          type: "button",
          className: STYLES.CLEAR_BUTTON,
          disabled,
          onClick: handleClearInput,
          children: /* @__PURE__ */ jsx42(CloseSquareIcon2, { className: "clear-icon-button" })
        }
      )
    ] }),
    isOpen && isUserTyping && /* @__PURE__ */ jsx42(
      "div",
      {
        className: STYLES.SUGGESTION_CONTAINER,
        ref: suggestionsRef,
        role: "listbox",
        "aria-label": "Address suggestions",
        id: "address-suggestions",
        children: /* @__PURE__ */ jsx42("ul", { className: "py-1", children: renderAddressSuggestions() })
      }
    )
  ] });
};
AddressAutocomplete.displayName = "AddressAutocomplete";
var AddressAutocomplete_default = AddressAutocomplete;

// src/components/common/LoadingOverlay/index.tsx
import { useEffect as useEffect7 } from "react";
import { usePathname } from "next/navigation";
import { Fragment as Fragment4, jsx as jsx43, jsxs as jsxs22 } from "react/jsx-runtime";
var LoadingOverlay = ({ isVisible, className = "" }) => {
  const pathname = usePathname();
  useEffect7(() => {
    const sidebarItems = Array.from(document.querySelectorAll("#sidebar-item"));
    sidebarItems.forEach((item) => {
      const isCurrentPath = item.getAttribute("href") === pathname;
      isVisible && !isCurrentPath ? item.classList.add("opacity-50") : item.classList.remove("opacity-50");
    });
    return () => sidebarItems.forEach((item) => item.classList.remove("opacity-50"));
  }, [isVisible, pathname]);
  if (!isVisible) return null;
  return /* @__PURE__ */ jsxs22(Fragment4, { children: [
    /* @__PURE__ */ jsx43("div", { className: "fixed inset-0 bg-transparent cursor-not-allowed z-40" }),
    /* @__PURE__ */ jsx43("div", { className: "fixed inset-0 bg-white/60 flex-center z-30 lg:z-20" }),
    /* @__PURE__ */ jsx43(
      LoadingIndicator_default,
      {
        className: `fixed inset-0 flex-center h-screen z-30 ${className}`,
        color: "primary",
        size: "lg"
      }
    )
  ] });
};
var LoadingOverlay_default = LoadingOverlay;

// src/components/DatePicker/index.tsx
import { useState as useState9, useMemo as useMemo2, useEffect as useEffect8 } from "react";
import dayjs3 from "dayjs";

// src/components/DatePicker/DatePickerContent.tsx
import dayjs from "dayjs";
import { MONTHS, DATE_CONSTANTS } from "@shared/constants";
import { combineClasses as combineClasses39, getSelectOptions } from "@shared/utils";
import ChevronLeftIcon2 from "@shared/icons/ChevronLeftIcon";
import ChevronRightIcon3 from "@shared/icons/ChevronRightIcon";
import { Fragment as Fragment5, jsx as jsx44, jsxs as jsxs23 } from "react/jsx-runtime";
var MonthGrid = ({ date, onMonthChange }) => /* @__PURE__ */ jsx44("div", { "aria-label": "Month selection", className: "grid grid-cols-4 gap-2", children: MONTHS.map((month) => {
  const isSelected = date && dayjs(date).month() === MONTHS.indexOf(month);
  return /* @__PURE__ */ jsx44(
    "button",
    {
      "aria-selected": isSelected,
      "aria-label": `Select ${month}`,
      className: combineClasses39(
        "p-1 rounded-md text-3xs text-frost-900 transition-colors",
        "hover:bg-primary-100",
        "focus:outline-none focus:ring-1 focus:ring-primary-100",
        isSelected ? "bg-secondary text-white hover:bg-secondary/80" : "bg-white"
      ),
      onClick: () => onMonthChange(month),
      children: month.slice(0, DATE_CONSTANTS.MONTH_DISPLAY_LENGTH)
    },
    month
  );
}) });
MonthGrid.displayName = "MonthGrid";
var MonthYearMode = ({
  date,
  years,
  selectedYear,
  isDateBeforeStartYear,
  isDateCurrentYearOrBeyond,
  onPreviousYear,
  onNextYear,
  onYearChange,
  onMonthChange,
  onClearDate
}) => /* @__PURE__ */ jsxs23(Fragment5, { children: [
  /* @__PURE__ */ jsxs23("div", { className: "space-y-4", children: [
    /* @__PURE__ */ jsxs23("div", { className: "w-full flex justify-between items-center", children: [
      /* @__PURE__ */ jsx44(
        "button",
        {
          "aria-label": "Previous Year",
          "data-testid": "left-prev-icon",
          disabled: isDateBeforeStartYear,
          className: combineClasses39(
            isDateBeforeStartYear && "cursor-not-allowed opacity-30"
          ),
          onClick: onPreviousYear,
          children: /* @__PURE__ */ jsx44(ChevronLeftIcon2, { className: "size-5 stroke-slate-500 opacity-50" })
        }
      ),
      /* @__PURE__ */ jsx44(
        Select_default2,
        {
          triggerClassName: combineClasses39(
            "text-secondary text-3xs font-semibold tracking-[5px]",
            "justify-center ring-0 hover:ring-0 focus:ring-0"
          ),
          hideTriggerIcon: true,
          options: getSelectOptions(years),
          value: selectedYear,
          onChange: onYearChange
        },
        selectedYear
      ),
      /* @__PURE__ */ jsx44(
        "button",
        {
          "aria-label": "Next Year",
          "data-testid": "right-next-icon",
          disabled: isDateCurrentYearOrBeyond,
          className: combineClasses39(
            isDateCurrentYearOrBeyond && "cursor-not-allowed opacity-30"
          ),
          onClick: onNextYear,
          children: /* @__PURE__ */ jsx44(ChevronRightIcon3, { className: "size-5 stroke-slate-500 opacity-50" })
        }
      )
    ] }),
    /* @__PURE__ */ jsx44(MonthGrid, { date, onMonthChange })
  ] }),
  /* @__PURE__ */ jsx44("div", { className: "w-full flex justify-end mt-2", children: /* @__PURE__ */ jsx44(
    "button",
    {
      "aria-label": "Clear Date Selection",
      className: "text-4xs font-semibold text-info cursor-pointer",
      onClick: onClearDate,
      children: "Clear"
    }
  ) })
] });
MonthYearMode.displayName = "MonthYearMode";
var DateDialogMode = ({
  date,
  month,
  disabledDates,
  onSelectedDate,
  onPreviousMonth,
  onNextMonth,
  onPopoverOpenChange
}) => {
  const handleSelect = (date2) => {
    onPopoverOpenChange?.(false);
    onSelectedDate?.(date2);
  };
  return /* @__PURE__ */ jsx44(
    Calendar,
    {
      "data-testid": "calendar-dialog-display",
      className: "rounded-md border-none shadow-none",
      mode: "single",
      month,
      initialFocus: true,
      variant: "compact",
      selected: date,
      disabled: disabledDates,
      onSelect: handleSelect,
      onPreviousMonth,
      onNextMonth
    }
  );
};
DateDialogMode.displayName = "DateDialogMode";
var DatetimeMode = ({
  date,
  month,
  disabledDates,
  hours,
  minutes,
  periods,
  timeValues,
  cancelButtonLabel,
  submitButtonLabel,
  cancelButtonClassName,
  submitButtonClassName,
  disableSubmitButton,
  onDatetimeDateSelect,
  onPreviousMonth,
  onNextMonth,
  onHourChange,
  onMinuteChange,
  onPeriodChange,
  onCancel,
  onSubmit
}) => /* @__PURE__ */ jsxs23("div", { className: "flex flex-col", "data-testid": "datetime-picker", children: [
  /* @__PURE__ */ jsx44(
    Calendar,
    {
      "data-testid": "calendar-datetime-display",
      className: "rounded-md border-none shadow-none",
      mode: "single",
      month,
      initialFocus: true,
      variant: "compact",
      selected: date,
      disabled: disabledDates,
      onSelect: onDatetimeDateSelect,
      onPreviousMonth,
      onNextMonth
    }
  ),
  /* @__PURE__ */ jsx44("div", { className: "px-5 py-3 border-t border-frost-200", children: /* @__PURE__ */ jsxs23("div", { className: "flex items-center justify-between", children: [
    /* @__PURE__ */ jsx44("span", { className: "text-3xs font-medium text-frost-700", children: "Time" }),
    /* @__PURE__ */ jsxs23("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsx44(
        Select_default2,
        {
          "data-testid": "datetime-hour-select",
          triggerClassName: "w-16 h-9 text-center gap-1 justify-center [&>svg]:size-4",
          options: hours,
          value: timeValues.hour,
          onChange: onHourChange,
          placeholder: "HH"
        }
      ),
      /* @__PURE__ */ jsx44("span", { className: "text-frost-500 font-medium", children: ":" }),
      /* @__PURE__ */ jsx44(
        Select_default2,
        {
          "data-testid": "datetime-minute-select",
          triggerClassName: "w-16 h-9 text-center gap-1 justify-center [&>svg]:size-4",
          options: minutes,
          value: timeValues.minute,
          onChange: onMinuteChange,
          placeholder: "MM"
        }
      ),
      /* @__PURE__ */ jsx44(
        Select_default2,
        {
          "data-testid": "datetime-period-select",
          triggerClassName: "w-16 h-9 text-center gap-1 justify-center [&>svg]:size-4",
          options: periods,
          value: timeValues.period,
          onChange: onPeriodChange
        }
      )
    ] })
  ] }) }),
  /* @__PURE__ */ jsxs23("div", { className: "flex gap-5 px-5 pb-4 pt-2", children: [
    /* @__PURE__ */ jsx44(
      "button",
      {
        type: "button",
        "aria-label": cancelButtonLabel,
        className: combineClasses39(
          "flex-1 rounded-lg text-xs font-medium",
          "border border-ocean-200 text-frost-900",
          "hover:bg-frost-50/80 transition-colors",
          "focus:outline-none",
          cancelButtonClassName
        ),
        onClick: onCancel,
        children: cancelButtonLabel
      }
    ),
    /* @__PURE__ */ jsx44(
      "button",
      {
        type: "button",
        "aria-label": submitButtonLabel,
        className: combineClasses39(
          "flex-1 h-11 rounded-lg text-xs font-medium",
          "bg-ocean-600 text-white",
          "hover:bg-ocean-600/90 transition-colors",
          submitButtonClassName
        ),
        onClick: onSubmit,
        disabled: disableSubmitButton,
        children: submitButtonLabel
      }
    )
  ] })
] });
DatetimeMode.displayName = "DatetimeMode";

// src/components/DatePicker/index.tsx
import {
  MONTHS as MONTHS2,
  DATE_CONSTANTS as DATE_CONSTANTS2,
  DATE_PICKER_DEFAULTS,
  DATE_PICKER_MODES
} from "@shared/constants";
import {
  generateYearOptions,
  createDateWithMonthYear,
  getSelectOptions as getSelectOptions2,
  combineClasses as combineClasses40,
  getPreviousMonth,
  getNextMonth,
  getNextYear,
  getPreviousYear,
  getCurrentYear,
  formatDisplayValue,
  isCurrentYearOrBeyond,
  isBeforeStartYear,
  getSelectedMonth,
  getSelectedYear
} from "@shared/utils";
import CalendarIcon from "@shared/icons/CalendarIcon";

// src/components/DatePicker/utils.ts
import dayjs2 from "dayjs";
var getTimeFromDate = (currentDate) => {
  const d = dayjs2(currentDate || /* @__PURE__ */ new Date());
  const hour24 = d.hour();
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  return {
    hour: String(hour12).padStart(2, "0"),
    minute: String(d.minute()).padStart(2, "0"),
    period: hour24 >= 12 ? "PM" : "AM"
  };
};

// src/components/DatePicker/index.tsx
import { Fragment as Fragment6, jsx as jsx45, jsxs as jsxs24 } from "react/jsx-runtime";
var DatePicker = ({
  date,
  month,
  triggerElement,
  placeholder = DATE_PICKER_DEFAULTS.PLACEHOLDER,
  className,
  disabled = false,
  mode = DATE_PICKER_DEFAULTS.DEFAULT_MODE,
  disabledDates,
  allowedHours,
  onDateChange,
  onPopoverClose,
  onPopoverOpen,
  onSelectedDate,
  cancelButtonClassName,
  submitButtonClassName,
  disableSubmitButton = false,
  cancelButtonLabel = "Cancel",
  submitButtonLabel = "Done",
  onCancel,
  onSubmit
}) => {
  const [isOpen, setIsOpen] = useState9(false);
  const [internalMonth, setInternalMonth] = useState9(
    () => date || month || /* @__PURE__ */ new Date()
  );
  const currentYear = getCurrentYear();
  const years = generateYearOptions(DATE_CONSTANTS2.START_YEAR, currentYear).map(
    (year) => String(year)
  );
  const isDateCurrentYearOrBeyond = isCurrentYearOrBeyond(date);
  const isDateBeforeStartYear = isBeforeStartYear(
    date,
    DATE_CONSTANTS2.START_YEAR
  );
  const displayValue = date ? formatDisplayValue(date, mode) : "";
  const displayedMonth = month || internalMonth;
  const selectedMonth = getSelectedMonth(date);
  const selectedYear = getSelectedYear(date);
  useEffect8(() => {
    if (date && !month) {
      setInternalMonth(date);
    }
  }, [date, month]);
  const timeValues = getTimeFromDate(date);
  const hours = useMemo2(() => {
    const allHours = Array.from({ length: 12 }, (_, i) => {
      const hour12 = i + 1;
      const hourValue = String(hour12).padStart(2, "0");
      let isDisabled = false;
      if (allowedHours) {
        const { minHour = 0, maxHour = 23 } = allowedHours;
        const currentPeriod = timeValues.period;
        let hour24;
        if (currentPeriod === "AM") {
          hour24 = hour12 === 12 ? 0 : hour12;
        } else {
          hour24 = hour12 === 12 ? 12 : hour12 + 12;
        }
        isDisabled = hour24 < minHour || hour24 > maxHour;
      }
      return {
        value: hourValue,
        label: hourValue,
        disabled: isDisabled
      };
    });
    return allHours;
  }, [allowedHours, timeValues.period]);
  const minutes = useMemo2(() => {
    const allMinutes = Array.from({ length: 60 }, (_, i) => {
      const minuteValue = String(i).padStart(2, "0");
      let isDisabled = false;
      if (allowedHours) {
        const timeValues2 = getTimeFromDate(date);
        const currentHour = parseInt(timeValues2.hour, 10);
        const currentPeriod = timeValues2.period;
        let hour24 = currentHour;
        if (currentPeriod === "PM" && currentHour !== 12) {
          hour24 = currentHour + 12;
        } else if (currentPeriod === "AM" && currentHour === 12) {
          hour24 = 0;
        }
        const {
          minHour = 0,
          minMinute,
          maxHour = 23,
          maxMinute
        } = allowedHours;
        if (hour24 === maxHour && maxMinute !== void 0) {
          isDisabled = i > maxMinute;
        }
        if (hour24 === minHour && minMinute !== void 0) {
          isDisabled = isDisabled || i < minMinute;
        }
      }
      return {
        value: minuteValue,
        label: minuteValue,
        disabled: isDisabled
      };
    });
    return allMinutes;
  }, [allowedHours, date]);
  const periods = useMemo2(() => {
    const allPeriods = [
      { value: "AM", label: "AM" },
      { value: "PM", label: "PM" }
    ];
    if (!allowedHours) return allPeriods;
    const { minHour = 0, maxHour = 23 } = allowedHours;
    return allPeriods.map((period) => {
      let isDisabled = false;
      if (period.value === "AM") {
        isDisabled = minHour >= 12 && maxHour >= 12;
      } else {
        isDisabled = maxHour < 12;
      }
      return {
        ...period,
        disabled: isDisabled
      };
    });
  }, [allowedHours]);
  const handleMonthChange = (month2) => {
    const updatedDate = createDateWithMonthYear(date, month2);
    setInternalMonth(updatedDate);
    onDateChange?.(updatedDate);
  };
  const handleYearChange = (year) => {
    const updatedDate = createDateWithMonthYear(
      date,
      dayjs3(date || /* @__PURE__ */ new Date()).month(),
      year
    );
    setInternalMonth(updatedDate);
    onDateChange?.(updatedDate);
  };
  const handlePreviousYear = () => {
    const updatedYear = getPreviousYear(date);
    if (dayjs3(updatedYear).year() < DATE_CONSTANTS2.START_YEAR) {
      return;
    }
    onDateChange?.(updatedYear);
  };
  const handleNextYear = () => {
    if (date && dayjs3(date).year() >= currentYear) {
      return;
    }
    const updatedYear = getNextYear(date);
    if (dayjs3(updatedYear).year() > currentYear) {
      return;
    }
    onDateChange?.(updatedYear);
  };
  const handleClearDate = () => {
    onDateChange?.(void 0);
  };
  const handlePreviousMonth = () => {
    const updatedMonth = getPreviousMonth(displayedMonth);
    setInternalMonth(updatedMonth);
    onDateChange?.(updatedMonth);
  };
  const handleNextMonth = () => {
    const updatedMonth = getNextMonth(displayedMonth);
    setInternalMonth(updatedMonth);
    onDateChange?.(updatedMonth);
  };
  const handlePopoverOpenChange = (open) => {
    setIsOpen(open);
    if (open) {
      onPopoverOpen?.();
    } else {
      onPopoverClose?.();
    }
  };
  const handleCancel = () => {
    setIsOpen(false);
    onCancel?.();
  };
  const handleSubmit = () => {
    setIsOpen(false);
    onSubmit?.();
  };
  const handleHourChange = (hour) => {
    const currentDate = date || /* @__PURE__ */ new Date();
    const currentPeriod = timeValues.period;
    let hour24 = parseInt(hour, 10);
    if (currentPeriod === "PM" && hour24 !== 12) {
      hour24 += 12;
    } else if (currentPeriod === "AM" && hour24 === 12) {
      hour24 = 0;
    }
    const updatedDate = dayjs3(currentDate).hour(hour24).toDate();
    onDateChange?.(updatedDate);
  };
  const handleMinuteChange = (minute) => {
    const currentDate = date || /* @__PURE__ */ new Date();
    const updatedDate = dayjs3(currentDate).minute(parseInt(minute, 10)).toDate();
    onDateChange?.(updatedDate);
  };
  const handlePeriodChange = (period) => {
    const currentDate = date || /* @__PURE__ */ new Date();
    const currentHour = dayjs3(currentDate).hour();
    let newHour = currentHour;
    if (period === "PM" && currentHour < 12) {
      newHour = currentHour + 12;
    } else if (period === "AM" && currentHour >= 12) {
      newHour = currentHour - 12;
    }
    const updatedDate = dayjs3(currentDate).hour(newHour).toDate();
    onDateChange?.(updatedDate);
  };
  const handleDatetimeDateSelect = (selectedDate) => {
    if (!selectedDate) {
      onSelectedDate?.(void 0);
      return;
    }
    if (date) {
      const existingTime = dayjs3(date);
      const updatedDate = dayjs3(selectedDate).hour(existingTime.hour()).minute(existingTime.minute()).second(0).toDate();
      onSelectedDate?.(updatedDate);
    } else {
      const now = dayjs3();
      const updatedDate = dayjs3(selectedDate).hour(now.hour()).minute(now.minute()).second(0).toDate();
      onSelectedDate?.(updatedDate);
    }
  };
  const renderContent = () => {
    const value = {
      date,
      month: displayedMonth,
      disabledDates,
      years,
      hours,
      minutes,
      periods,
      timeValues,
      selectedYear,
      isDateBeforeStartYear,
      isDateCurrentYearOrBeyond,
      cancelButtonLabel,
      submitButtonLabel,
      cancelButtonClassName,
      submitButtonClassName,
      disableSubmitButton,
      onPreviousYear: handlePreviousYear,
      onNextYear: handleNextYear,
      onYearChange: handleYearChange,
      onMonthChange: handleMonthChange,
      onClearDate: handleClearDate,
      onSelectedDate,
      onPreviousMonth: handlePreviousMonth,
      onNextMonth: handleNextMonth,
      onCancel: handleCancel,
      onSubmit: handleSubmit,
      onDatetimeDateSelect: handleDatetimeDateSelect,
      onHourChange: handleHourChange,
      onMinuteChange: handleMinuteChange,
      onPeriodChange: handlePeriodChange,
      onPopoverOpenChange: handlePopoverOpenChange
    };
    switch (mode) {
      case DATE_PICKER_MODES.MONTH_YEAR:
        return /* @__PURE__ */ jsx45(MonthYearMode, { ...value });
      case DATE_PICKER_MODES.DATE_DIALOG:
        return /* @__PURE__ */ jsx45(DateDialogMode, { ...value });
      case DATE_PICKER_MODES.DATETIME:
        return /* @__PURE__ */ jsx45(DatetimeMode, { ...value });
      case DATE_PICKER_MODES.DATE:
      default:
        return /* @__PURE__ */ jsxs24(Fragment6, { children: [
          /* @__PURE__ */ jsxs24("div", { className: "flex justify-between p-2 gap-2", children: [
            /* @__PURE__ */ jsx45(
              Select_default2,
              {
                options: getSelectOptions2(MONTHS2),
                value: selectedMonth,
                onChange: handleMonthChange,
                placeholder: "May"
              }
            ),
            /* @__PURE__ */ jsx45(
              Select_default2,
              {
                options: getSelectOptions2(years),
                value: selectedYear,
                onChange: handleYearChange,
                placeholder: "2025"
              }
            )
          ] }),
          /* @__PURE__ */ jsx45(
            Calendar,
            {
              "data-testid": "calendar-month-display",
              className: "rounded-md border-none shadow-none",
              mode: "single",
              month: displayedMonth,
              selected: date,
              disabled: disabledDates,
              autoFocus: false,
              onSelect: onDateChange,
              onPreviousMonth: handlePreviousMonth,
              onNextMonth: handleNextMonth
            }
          ),
          /* @__PURE__ */ jsx45("div", { className: "w-full flex justify-end px-4 pb-3.5", children: /* @__PURE__ */ jsx45(
            "button",
            {
              "aria-label": "Clear Date Selection",
              className: "text-4xs font-semibold text-info cursor-pointer",
              onClick: handleClearDate,
              children: "Clear"
            }
          ) })
        ] });
    }
  };
  return /* @__PURE__ */ jsxs24(Popover, { open: isOpen, onOpenChange: handlePopoverOpenChange, children: [
    /* @__PURE__ */ jsx45(PopoverTrigger, { asChild: true, children: triggerElement || /* @__PURE__ */ jsx45(
      "button",
      {
        type: "button",
        "aria-label": "Select date",
        disabled,
        className: combineClasses40(
          "flex items-center w-full h-[35px] px-2 rounded-md outline-none",
          "text-left text-frost-900 text-4xs bg-frost-100",
          "transition-all duration-200 ease-in-out",
          "focus:ring-1 focus:ring-primary-500",
          "hover:ring-1 hover:ring-frost-900",
          "placeholder:text-frost-400 placeholder:text-4xs",
          "focus:ring-none data-[state=open]:ring-1 data-[state=open]:ring-primary-500",
          disabled && "cursor-not-allowed",
          !date && "text-muted-foreground",
          className
        ),
        children: date ? displayValue : /* @__PURE__ */ jsxs24(Fragment6, { children: [
          /* @__PURE__ */ jsx45(CalendarIcon, { className: "mr-2 h-4 w-4 text-frost-300" }),
          /* @__PURE__ */ jsx45(
            "span",
            {
              className: combineClasses40(
                "font-normal text-4xs",
                "text-slate-500/50"
              ),
              children: placeholder
            }
          )
        ] })
      }
    ) }),
    /* @__PURE__ */ jsx45(
      PopoverContent,
      {
        className: combineClasses40(
          "w-auto",
          mode === DATE_PICKER_MODES.MONTH_YEAR ? "p-4 w-date-picker" : "p-0 w-full max-w-date-picker md:w-date-picker-md",
          mode === DATE_PICKER_MODES.DATETIME && "min-w-[340px]"
        ),
        align: "start",
        children: renderContent()
      }
    )
  ] });
};
DatePicker.displayName = "DatePicker";

// src/components/RichTextEditor/LinkDialog/index.tsx
import { Range } from "slate";
import { jsx as jsx46, jsxs as jsxs25 } from "react/jsx-runtime";
var LinkDialog = ({
  open,
  editor,
  linkData,
  onInsert,
  onOpenChange,
  onLinkDataChange
}) => {
  const handleClose = () => onOpenChange(false);
  const handleUrlChange = (value) => {
    onLinkDataChange({ ...linkData, url: value });
  };
  const handleTextChange = (value) => {
    onLinkDataChange({ ...linkData, text: value });
  };
  return /* @__PURE__ */ jsx46(
    CustomDialog,
    {
      className: "link-dialog w-full max-w-md sm:p-8",
      open,
      onOpenChange,
      title: "Insert Link",
      footer: /* @__PURE__ */ jsxs25("div", { className: "flex justify-end gap-3 mt-6", children: [
        /* @__PURE__ */ jsx46(
          Button,
          {
            className: "h-12",
            variant: "outline",
            size: "sm",
            onClick: handleClose,
            children: "Cancel"
          }
        ),
        /* @__PURE__ */ jsx46(
          Button,
          {
            size: "sm",
            onClick: onInsert,
            className: "h-12 bg-primary hover:bg-primary/90",
            children: "Insert"
          }
        )
      ] }),
      children: /* @__PURE__ */ jsxs25("div", { className: "space-y-4", children: [
        /* @__PURE__ */ jsxs25("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx46(
            "label",
            {
              htmlFor: "url",
              className: "block text-sm font-medium text-slate-700",
              children: "URL"
            }
          ),
          /* @__PURE__ */ jsx46(
            Input,
            {
              id: "url",
              value: linkData.url,
              onChangeValue: handleUrlChange,
              placeholder: "https://github.com",
              className: "h-10 text-xs"
            }
          )
        ] }),
        editor.selection && Range.isCollapsed(editor.selection) && /* @__PURE__ */ jsxs25("div", { className: "space-y-2", children: [
          /* @__PURE__ */ jsx46(
            "label",
            {
              htmlFor: "text",
              className: "block text-sm font-medium text-slate-700",
              children: "Link Text"
            }
          ),
          /* @__PURE__ */ jsx46(
            Input,
            {
              id: "text",
              value: linkData.text || "",
              onChangeValue: handleTextChange,
              placeholder: "Github",
              className: "h-10 text-xs"
            }
          )
        ] })
      ] })
    }
  );
};

// src/components/RichTextEditor/Toolbar/index.tsx
import { useCallback as useCallback3, useState as useState10 } from "react";
import { Editor, Transforms, Range as Range2, Text } from "slate";
import { useSlate } from "slate-react";
import {
  BlockFormat,
  TextFormat
} from "@shared/types";
import {
  combineClasses as combineClasses42,
  toggleBlock,
  toggleMark,
  isBlockActive,
  isMarkActive
} from "@shared/utils";
import { TEXT_FORMATS, BLOCK_FORMATS } from "@shared/constants";
import LinkIcon from "@shared/icons/LinkIcon";

// src/components/RichTextEditor/Toolbar/ToolbarButton.tsx
import { combineClasses as combineClasses41 } from "@shared/utils";
import { jsx as jsx47, jsxs as jsxs26 } from "react/jsx-runtime";
var ToolbarButton = ({
  label,
  isActive,
  icon: Icon2,
  shortcut,
  onClick,
  disabled = false
}) => {
  const handleMouseDown = (e) => {
    e.preventDefault();
  };
  return /* @__PURE__ */ jsxs26(
    Button,
    {
      title: `${label}${shortcut ? ` (${shortcut})` : ""}`,
      size: "icon",
      type: "button",
      disabled,
      className: combineClasses41(
        "size-6 px-2 rounded-sm border-transparent text-frost-900",
        isActive && "text-primary-700",
        disabled && "opacity-40 cursor-not-allowed"
      ),
      variant: "ghost",
      onClick,
      onMouseDown: handleMouseDown,
      children: [
        /* @__PURE__ */ jsx47(Icon2, { className: "size-4" }),
        /* @__PURE__ */ jsx47("span", { className: "sr-only", children: label })
      ]
    }
  );
};

// src/components/RichTextEditor/Toolbar/index.tsx
import { Fragment as Fragment7, jsx as jsx48, jsxs as jsxs27 } from "react/jsx-runtime";
var insertLink = (editor, linkData) => {
  if (!linkData.url) return;
  const { selection } = editor;
  if (!selection) return;
  const isCollapsed = Range2.isCollapsed(selection);
  if (isCollapsed && linkData.text) {
    Transforms.insertNodes(editor, {
      text: linkData.text,
      url: linkData.url
    });
  } else {
    Transforms.setNodes(
      editor,
      { url: linkData.url },
      { match: (n) => Text.isText(n), split: true }
    );
  }
};
var isLinkActive = (editor) => {
  const { selection } = editor;
  if (!selection) return false;
  if (!Editor.hasPath(editor, selection.anchor.path)) return false;
  try {
    const [match] = Editor.nodes(editor, {
      match: (node) => !Editor.isEditor(node) && "url" in node && node.url !== void 0
    });
    return !!match;
  } catch {
    return false;
  }
};
var removeLinkAtSelection = (editor) => {
  Transforms.setNodes(
    editor,
    { url: null },
    { match: (n) => Text.isText(n) && !!n.url, split: true }
  );
};
var Toolbar = ({
  isBorder = false,
  className,
  disableInlineCode = false,
  disableCodeBlock = false,
  linkPosition = "end",
  separatorClassName = "bg-frost-900"
}) => {
  const editor = useSlate();
  const [showLinkDialog, setShowLinkDialog] = useState10(false);
  const [linkData, setLinkData] = useState10({ url: "" });
  const applyMark = useCallback3(
    (format) => toggleMark(editor, format),
    [editor]
  );
  const applyBlock = useCallback3(
    (format) => {
      toggleBlock(editor, format);
    },
    [editor]
  );
  const handleInsertLink = () => {
    insertLink(editor, linkData);
    setShowLinkDialog(false);
    setLinkData({ url: "" });
  };
  const handleLinkButtonClick = () => {
    if (isLinkActive(editor)) {
      removeLinkAtSelection(editor);
    } else {
      setShowLinkDialog(true);
    }
  };
  const handleToggleMark = (format) => () => applyMark(format);
  const handleToggleBlock = (format) => () => applyBlock(format);
  const isInCodeBlock = isBlockActive(editor, BlockFormat.CODE_BLOCK);
  const handleMouseDown = (e) => {
    e.preventDefault();
  };
  const visibleTextFormats = TEXT_FORMATS.filter(
    ({ format }) => !(disableInlineCode && format === TextFormat.CODE)
  );
  const visibleBlockFormats = BLOCK_FORMATS.filter(
    ({ format }) => !(disableCodeBlock && format === BlockFormat.CODE_BLOCK)
  );
  const renderBlockButton = ({
    format,
    icon,
    label,
    shortcut
  }) => /* @__PURE__ */ jsx48(
    ToolbarButton,
    {
      icon,
      label,
      shortcut,
      isActive: isBlockActive(editor, format),
      onClick: handleToggleBlock(format)
    },
    format
  );
  const linkButton = /* @__PURE__ */ jsx48(
    ToolbarButton,
    {
      icon: LinkIcon,
      label: "Insert Link",
      isActive: isLinkActive(editor),
      onClick: handleLinkButtonClick
    }
  );
  return /* @__PURE__ */ jsxs27(Fragment7, { children: [
    /* @__PURE__ */ jsxs27(
      "div",
      {
        className: combineClasses42(
          "flex flex-wrap gap-2.5",
          "rounded-t-md px-3.5 pt-4",
          isBorder ? "bg-frost-100 border-b border-frost-300 pb-3.5 mx-4" : "",
          className
        ),
        onMouseDown: handleMouseDown,
        children: [
          visibleTextFormats.map(({ format, icon, label, shortcut }) => /* @__PURE__ */ jsx48(
            ToolbarButton,
            {
              icon,
              label,
              shortcut,
              isActive: isMarkActive(editor, format),
              disabled: isInCodeBlock,
              onClick: handleToggleMark(format)
            },
            format
          )),
          /* @__PURE__ */ jsx48(
            "div",
            {
              className: combineClasses42("w-px h-6 mx-1.5", separatorClassName)
            }
          ),
          linkPosition === "middle" ? /* @__PURE__ */ jsxs27(Fragment7, { children: [
            visibleBlockFormats.filter(
              ({ format }) => format === BlockFormat.NUMBERED_LIST || format === BlockFormat.BULLETED_LIST
            ).map(renderBlockButton),
            linkButton,
            visibleBlockFormats.filter(
              ({ format }) => format !== BlockFormat.NUMBERED_LIST && format !== BlockFormat.BULLETED_LIST
            ).map(renderBlockButton)
          ] }) : /* @__PURE__ */ jsxs27(Fragment7, { children: [
            visibleBlockFormats.map(renderBlockButton),
            linkButton
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsx48(
      LinkDialog,
      {
        "data-testid": "link-dialog",
        open: showLinkDialog,
        editor,
        linkData,
        onInsert: handleInsertLink,
        onLinkDataChange: setLinkData,
        onOpenChange: setShowLinkDialog
      }
    )
  ] });
};

// src/components/RichTextEditor/RichTextEditor.tsx
import {
  useMemo as useMemo3,
  useCallback as useCallback4,
  useEffect as useEffect9,
  useRef as useRef4,
  useState as useState11
} from "react";
import { toast } from "sonner";
import {
  createEditor,
  Element,
  Node,
  Transforms as Transforms2
} from "slate";
import {
  Slate,
  Editable,
  withReact
} from "slate-react";
import { withHistory } from "slate-history";
import {
  BlockFormat as BlockFormat2,
  TextFormat as TextFormat2,
  RichTextStylePreset
} from "@shared/types";
import {
  handleKeyboardShortcuts,
  isEqual as isEqual2,
  combineClasses as combineClasses43,
  countCharacters,
  isEditorEmpty
} from "@shared/utils";
import { MESSAGES as MESSAGES2 } from "@shared/constants";
import { Fragment as Fragment8, jsx as jsx49, jsxs as jsxs28 } from "react/jsx-runtime";
var withCodeBlockNormalizer = (editor) => {
  const { normalizeNode } = editor;
  editor.normalizeNode = (entry) => {
    const [node, path] = entry;
    if (Element.isElement(node) && "type" in node && node.type === BlockFormat2.CODE_BLOCK) {
      const invalidMarks = [
        TextFormat2.CODE,
        TextFormat2.BOLD,
        TextFormat2.ITALIC,
        TextFormat2.UNDERLINE
      ];
      for (const [child, childPath] of Node.texts(node)) {
        for (const mark of invalidMarks) {
          if (child[mark]) {
            Transforms2.unsetNodes(editor, mark, {
              at: [...path, ...childPath]
            });
            return;
          }
        }
      }
    }
    normalizeNode(entry);
  };
  return editor;
};
var DEFAULT_ELEMENT_STYLES = {
  [BlockFormat2.HEADING_1]: "text-2xl md:text-3xl xl:text-[28px] font-semibold mb-4",
  [BlockFormat2.HEADING_2]: "text-xl md:text-2xl xl:text-3xl font-semibold mb-3",
  [BlockFormat2.BLOCK_QUOTE]: "border-l-4 border-slate-300 pl-4 italic text-frost-900 mt-4 mb-0 [&+&]:mt-0",
  [BlockFormat2.CODE_BLOCK]: "bg-frost-400 p-4 rounded-md text-4xs my-4 whitespace-pre-wrap",
  [BlockFormat2.BULLETED_LIST]: "list-disc list-inside space-y-1 pl-4 my-4",
  [BlockFormat2.NUMBERED_LIST]: "list-decimal list-inside space-y-1 pl-4 my-4",
  [BlockFormat2.LIST_ITEM]: "leading-relaxed text-frost-900 marker:text-frost-900"
};
var CHATBOT_ELEMENT_STYLES = {
  [BlockFormat2.HEADING_1]: "text-2xl md:text-3xl xl:text-[28px] font-semibold mb-4",
  [BlockFormat2.HEADING_2]: "text-xl md:text-2xl xl:text-3xl font-semibold mb-3",
  [BlockFormat2.BLOCK_QUOTE]: "border-l-4 border-slate-300 pl-4 italic text-frost-900 mt-4 mb-0 [&+&]:mt-0",
  [BlockFormat2.CODE_BLOCK]: "bg-frost-900 text-white p-4 rounded-md text-4xs my-4 whitespace-pre-wrap",
  [BlockFormat2.BULLETED_LIST]: "list-disc list-inside space-y-1 pl-4 my-4",
  [BlockFormat2.NUMBERED_LIST]: "list-decimal list-inside space-y-1 pl-4 my-4",
  [BlockFormat2.LIST_ITEM]: "leading-relaxed text-frost-900 marker:text-frost-900"
};
var getElementStyles = (preset = RichTextStylePreset.DEFAULT) => {
  switch (preset) {
    case RichTextStylePreset.CHATBOT:
      return CHATBOT_ELEMENT_STYLES;
    default:
      return DEFAULT_ELEMENT_STYLES;
  }
};
var EditorFooter = ({
  current,
  max,
  errorMessage,
  error = false,
  className
}) => {
  const isNearLimit = max - current <= 50;
  const isAtLimit = max - current <= 0;
  return /* @__PURE__ */ jsxs28(
    "div",
    {
      className: combineClasses43(
        "pt-2 flex justify-between",
        "text-frost-900 text-4xs leading-xs gap-4",
        isAtLimit && "text-destructive",
        isNearLimit && "text-info",
        error && "text-destructive",
        className
      ),
      children: [
        /* @__PURE__ */ jsx49("p", { className: "text-destructive whitespace-pre-line text-4xs leading-normal mt-[1px]", children: errorMessage }),
        max > 0 && /* @__PURE__ */ jsxs28(
          "p",
          {
            "data-testid": "character-counter",
            className: "font-medium text-4xs leading-xs text-frost-900 flex-shrink-0",
            children: [
              /* @__PURE__ */ jsx49("span", { className: "text-primary-700", children: current }),
              " / ",
              max
            ]
          }
        )
      ]
    }
  );
};
var RenderElement = ({
  attributes,
  children,
  element,
  stylePreset = RichTextStylePreset.DEFAULT
}) => {
  const elementType = element.type;
  const styles = getElementStyles(stylePreset);
  const className = styles[elementType];
  switch (elementType) {
    case BlockFormat2.HEADING_1:
      return /* @__PURE__ */ jsx49("h1", { className, ...attributes, children });
    case BlockFormat2.HEADING_2:
      return /* @__PURE__ */ jsx49("h2", { className, ...attributes, children });
    case BlockFormat2.BLOCK_QUOTE:
      return /* @__PURE__ */ jsx49("blockquote", { className, ...attributes, children });
    case BlockFormat2.CODE_BLOCK: {
      const wrapper = element.wrapper;
      const codeClass = wrapper === BlockFormat2.HEADING_1 || wrapper === BlockFormat2.HEADING_2 ? className.replace(/text-4xs\s?/, "") : className;
      const codeEl = /* @__PURE__ */ jsx49("pre", { className: codeClass, ...attributes, children: /* @__PURE__ */ jsx49("code", { children }) });
      if (wrapper === BlockFormat2.BLOCK_QUOTE) {
        return /* @__PURE__ */ jsx49("blockquote", { className: styles[BlockFormat2.BLOCK_QUOTE], children: codeEl });
      }
      if (wrapper === BlockFormat2.HEADING_1) {
        return /* @__PURE__ */ jsx49("h1", { className: styles[BlockFormat2.HEADING_1], children: codeEl });
      }
      if (wrapper === BlockFormat2.HEADING_2) {
        return /* @__PURE__ */ jsx49("h2", { className: styles[BlockFormat2.HEADING_2], children: codeEl });
      }
      return codeEl;
    }
    case BlockFormat2.BULLETED_LIST: {
      const listEl = /* @__PURE__ */ jsx49("ul", { className, ...attributes, children });
      const wrapper = element.wrapper;
      if (wrapper === BlockFormat2.BLOCK_QUOTE) {
        return /* @__PURE__ */ jsx49("blockquote", { className: styles[BlockFormat2.BLOCK_QUOTE], children: listEl });
      }
      return listEl;
    }
    case BlockFormat2.NUMBERED_LIST: {
      const listEl = /* @__PURE__ */ jsx49("ol", { className, ...attributes, children });
      const wrapper = element.wrapper;
      if (wrapper === BlockFormat2.BLOCK_QUOTE) {
        return /* @__PURE__ */ jsx49("blockquote", { className: styles[BlockFormat2.BLOCK_QUOTE], children: listEl });
      }
      return listEl;
    }
    case BlockFormat2.LIST_ITEM:
      return /* @__PURE__ */ jsx49("li", { className, ...attributes, children });
    default:
      return /* @__PURE__ */ jsx49("p", { className, ...attributes, children });
  }
};
var getLeafStyles = (preset = RichTextStylePreset.DEFAULT) => {
  switch (preset) {
    case RichTextStylePreset.CHATBOT:
      return {
        code: "bg-frost-900 text-white px-1.5 py-0.5 rounded",
        link: "text-info hover:text-info/80 underline transition-colors"
      };
    default:
      return {
        code: "bg-frost-500 px-1.5 py-0.5 rounded text-secondary-foreground",
        link: "text-info hover:text-info/80 underline transition-colors"
      };
  }
};
var RenderLeaf = ({
  attributes,
  children,
  leaf,
  stylePreset = RichTextStylePreset.DEFAULT,
  disableInlineCode = false
}) => {
  const leafStyles = getLeafStyles(stylePreset);
  let formattedContent = children;
  if (leaf.bold) {
    formattedContent = /* @__PURE__ */ jsx49("strong", { className: "font-semibold", children: formattedContent });
  }
  if (leaf.italic) {
    formattedContent = /* @__PURE__ */ jsx49("em", { children: formattedContent });
  }
  if (leaf.underline) {
    formattedContent = /* @__PURE__ */ jsx49("u", { children: formattedContent });
  }
  if (leaf.code && !disableInlineCode) {
    formattedContent = /* @__PURE__ */ jsx49("code", { className: leafStyles.code, children: formattedContent });
  }
  if (leaf.url) {
    formattedContent = /* @__PURE__ */ jsx49(
      "a",
      {
        href: leaf.url,
        className: leafStyles.link,
        target: leaf.url.startsWith("http") ? "_blank" : "_self",
        rel: "noopener noreferrer",
        children: formattedContent
      }
    );
  }
  return /* @__PURE__ */ jsx49("span", { ...attributes, children: formattedContent });
};
var RichTextEditor = ({
  isAction = false,
  isSubmitting = false,
  isDisabledSubmit = false,
  isDisabledCancel = false,
  headerElement,
  value,
  className,
  disabled,
  placeholder,
  toolbarClassName,
  maxCharacters = 0,
  stylePreset = RichTextStylePreset.DEFAULT,
  disableInlineCode = false,
  disableCodeBlock = false,
  onChange,
  onFocus,
  onBlur,
  error,
  onSubmit,
  onCancel,
  confirmBtnClassName,
  cancelBtnClassName,
  errorMessage,
  linkPosition,
  separatorClassName
}) => {
  const previousValueRef = useRef4(value);
  const editor = useMemo3(
    () => withCodeBlockNormalizer(withHistory(withReact(createEditor()))),
    []
  );
  const [isFocused, setIsFocused] = useState11(false);
  const characterCount = useMemo3(() => {
    return countCharacters(value);
  }, [value]);
  const isEmpty = useMemo3(() => {
    return isEditorEmpty(value);
  }, [value]);
  const showPlaceholder = isEmpty && !isFocused && placeholder;
  const slateKeyRef = useRef4("");
  const slateKey = useMemo3(() => {
    if (isFocused) return slateKeyRef.current;
    try {
      slateKeyRef.current = JSON.stringify(value);
    } catch (error2) {
      const message = error2 instanceof Error ? error2.message : MESSAGES2.GENERAL_ERROR;
      toast.error(message);
    }
    return slateKeyRef.current;
  }, [value, isFocused]);
  useEffect9(() => {
    if (editor.selection) Transforms2.deselect(editor);
  }, [slateKey]);
  const handleKeyDown = useCallback4(
    (event) => {
      if (!disabled)
        handleKeyboardShortcuts(event, editor, {
          disableInlineCode,
          disableCodeBlock
        });
    },
    [editor, disabled, disableInlineCode, disableCodeBlock]
  );
  const handleOnChange = (value2) => {
    const newValue = value2;
    if (!isEqual2(newValue, previousValueRef.current)) {
      previousValueRef.current = newValue;
      onChange(newValue);
    }
  };
  const blurTimeoutRef = useRef4(void 0);
  const handleFocus = (e) => {
    clearTimeout(blurTimeoutRef.current);
    setIsFocused(true);
    onFocus?.(e);
  };
  const handleBlur = (e) => {
    clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = setTimeout(() => {
      setIsFocused(false);
      onBlur?.(e);
    }, 150);
  };
  return /* @__PURE__ */ jsxs28(Fragment8, { children: [
    /* @__PURE__ */ jsxs28(
      "div",
      {
        className: combineClasses43(
          "outline-none rounded-md overflow-hidden bg-frost-100",
          "text-4xs leading-xs text-frost-900 transition-colors",
          "hover:ring-1 hover:ring-frost-900",
          isFocused && "ring-1 ring-primary-500 hover:ring-primary-500",
          characterCount > maxCharacters && "border-destructive",
          disabled && "opacity-50 cursor-not-allowed",
          error && "ring-1 ring-destructive",
          className
        ),
        children: [
          headerElement,
          /* @__PURE__ */ jsxs28(
            Slate,
            {
              editor,
              initialValue: value,
              onChange: handleOnChange,
              children: [
                /* @__PURE__ */ jsx49(
                  Toolbar,
                  {
                    isBorder: true,
                    className: toolbarClassName,
                    disableInlineCode,
                    disableCodeBlock,
                    linkPosition,
                    separatorClassName
                  }
                ),
                /* @__PURE__ */ jsxs28(
                  "div",
                  {
                    "aria-label": "Text Editor Wrapper",
                    onBlur: handleBlur,
                    className: "relative",
                    children: [
                      showPlaceholder && /* @__PURE__ */ jsx49(
                        "p",
                        {
                          "data-testid": "rich-text-editor-placeholder",
                          className: "absolute left-5 top-3.5 text-frost-500 text-4xs leading-xs pointer-events-none select-none",
                          children: placeholder
                        }
                      ),
                      /* @__PURE__ */ jsx49(
                        Editable,
                        {
                          className: combineClasses43(
                            "px-5 py-3.5 min-h-40 rounded-b-md overflow-y-auto small-scrollbar",
                            "text-frost-900 text-4xs leading-xs",
                            "focus:outline-none",
                            disabled && "opacity-50 cursor-not-allowed"
                          ),
                          "aria-label": "Text Editor",
                          renderElement: (props) => /* @__PURE__ */ jsx49(RenderElement, { ...props, stylePreset }),
                          renderLeaf: (props) => /* @__PURE__ */ jsx49(
                            RenderLeaf,
                            {
                              ...props,
                              stylePreset,
                              disableInlineCode
                            }
                          ),
                          readOnly: disabled,
                          onKeyDown: handleKeyDown,
                          onFocus: handleFocus
                        }
                      )
                    ]
                  }
                )
              ]
            },
            slateKey
          )
        ]
      }
    ),
    isAction && /* @__PURE__ */ jsxs28("div", { className: "mt-2.5 flex flex-col md:flex-row gap-2 md:gap-5 justify-between mb-5 md:items-center", children: [
      /* @__PURE__ */ jsx49("div", { children: /* @__PURE__ */ jsx49(
        EditorFooter,
        {
          current: characterCount,
          max: maxCharacters,
          error,
          errorMessage,
          className: "pt-0"
        }
      ) }),
      /* @__PURE__ */ jsxs28("div", { className: "flex gap-5 self-end", children: [
        /* @__PURE__ */ jsx49(
          Button,
          {
            disabled: isDisabledCancel,
            variant: "primary",
            className: combineClasses43(
              "bg-frost-150 rounded-sm hover:bg-frost-150/80 uppercase text-frost-900",
              "font-semibold border-ocean-600 text-3xs py-4 px-2 h-7",
              cancelBtnClassName
            ),
            onClick: onCancel,
            children: "cancel"
          }
        ),
        /* @__PURE__ */ jsx49(
          Button,
          {
            disabled: isDisabledSubmit,
            isLoading: isSubmitting,
            variant: "primary",
            className: combineClasses43(
              "bg-ocean-600 rounded-sm uppercase text-3xs font-semibold py-4 px-2",
              "h-7 hover:bg-ocean-600/80 text-white",
              confirmBtnClassName
            ),
            onClick: onSubmit,
            children: "save"
          }
        )
      ] })
    ] }),
    !isAction && /* @__PURE__ */ jsx49(
      EditorFooter,
      {
        current: characterCount,
        max: maxCharacters,
        error,
        errorMessage
      }
    )
  ] });
};

// src/components/PDFNoData/index.tsx
import FileIcon from "@shared/icons/FileIcon";
import { jsx as jsx50, jsxs as jsxs29 } from "react/jsx-runtime";
var PDFNoData = () => {
  return /* @__PURE__ */ jsx50("div", { className: "w-full flex-center absolute inset-0", children: /* @__PURE__ */ jsxs29("div", { className: "flex-center flex-col gap-2 p-4 sm:p-6 text-center", children: [
    /* @__PURE__ */ jsx50("div", { className: "rounded-full bg-muted/30 p-3", children: /* @__PURE__ */ jsx50(FileIcon, { className: "!size-6" }) }),
    /* @__PURE__ */ jsxs29("div", { className: "flex flex-col items-center text-center", children: [
      /* @__PURE__ */ jsx50(
        Typography_default,
        {
          as: "span",
          weight: "semibold",
          className: "text-4xs lg:text-2xs leading-normal mb-0.5 sm:mb-1",
          children: "No PDF file specified"
        }
      ),
      /* @__PURE__ */ jsx50(
        Typography_default,
        {
          as: "span",
          color: "secondary",
          className: "text-4xs sm:text-3xs leading-normal",
          children: "Please check your PDF file and try again"
        }
      )
    ] })
  ] }) });
};
var PDFNoData_default = PDFNoData;

// src/components/ReactScan/index.tsx
import { useEffect as useEffect10 } from "react";
import { scan } from "react-scan";
var ReactScan = () => {
  useEffect10(() => {
    scan({
      // eslint-disable-next-line turbo/no-undeclared-env-vars
      enabled: process.env.NODE_ENV === "development"
    });
  }, []);
  return null;
};
var ReactScan_default = ReactScan;
export {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  AddressAutocomplete_default as AddressAutocomplete,
  AutoComplete_default as AutoComplete,
  Badge,
  BrandIcon_default as BrandIcon,
  Button,
  Calendar,
  Card,
  Checkbox_default as Checkbox,
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
  ConfirmDialog,
  CustomDialog,
  DatePicker,
  DialogBase,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  EditorFooter,
  FabMenu_default as FabMenu,
  ImageCustom_default as ImageCustom,
  ImageFallback_default as ImageFallback,
  Input,
  LinkDialog,
  LoadingIndicator_default as LoadingIndicator,
  LoadingOverlay_default as LoadingOverlay,
  PDFNoData_default as PDFNoData,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  ReactScan_default as ReactScan,
  RenderElement,
  RenderLeaf,
  RichTextEditor,
  Select_default2 as Select,
  Separator2 as Separator,
  Skeleton_default as Skeleton,
  Switch,
  Toaster_default as Toaster,
  Toolbar,
  Tooltip,
  TruncatedText_default as TruncatedText,
  Typography_default as Typography,
  badgeVariants,
  buttonVariants,
  insertLink
};
